import React, { useEffect, useMemo, useState } from 'react';
import { AccessibilityInfo, Platform, Pressable, ScrollView, View } from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import { useRouter } from 'expo-router';
import MetaHead from '@/components/MetaHead';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import Icon from '@/components/Icon';
import PublicFooter from '@/components/PublicFooter';
import { isDarkTheme } from '@/constants/Colors';
import FlowField from '@/components/Landing/FlowField';
import { PETS } from '@/constants/pets';
import ChangelogHighlights from '@/components/Changelog/ChangelogHighlights';
import { accentAlpha, landingPalette } from '@/components/Landing/landingTokens';

// Accents lifted from the real chat cards so the mockups read as the real thing.
const RECIPE_ACCENT = '#d9903d';
const WORKOUT_ACCENT = '#4c9b5f';
// Accents for the three editable artifact kinds — match real editor chrome.
const DOC_ACCENT = '#3157a4';
const SLIDE_ACCENT = '#d9482f';
const SHEET_ACCENT = '#16a34a';

const PET_FRAME_DURATION = 900;
const TIMELINE_PREVIEW_COUNT = 3;

const DREAM_ACCENT = '#8B5CF6';
const MUSIC_ACCENT = '#db2777';
// Voice-search SERP chrome matches the live Voice Mode panel.
const VOICE_SEARCH_ACCENT = '#7c3aed';

// Pin colors match the live map card so the mockup reads as the real widget.
const MAP_PIN = '#2563eb';
const MAP_PIN_SELECTED = '#178A68';

const IMPROVEMENT_CARDS = [
  {
    icon: 'desktop',
    title: 'Windows local Work',
    body: 'New Windows app candidates bring connected-project Work onto your PC, with local chat history, file changes, approved commands, document tools, and a dedicated browser.',
  },
  {
    icon: 'pen-to-square',
    title: 'Separate Chat & Work instructions',
    body: 'Save different custom instructions for everyday Chat replies and your Work agent in Personalization settings.',
  },
  {
    icon: 'credit-card',
    title: 'Subscription payments & receipts',
    body: 'Find recurring payments in billing history, with monthly Plus credits resetting at midnight Eastern.',
  },
  {
    icon: 'briefcase',
    title: 'Gab AI Business',
    body: 'Bring your team together with Plus and Plus Premium seats, shared company credits, invitations, spending limits, and usage reports. Manage setup and billing on the website.',
  },
  {
    icon: 'globe',
    title: 'Publish your creations as websites',
    body: 'Publish supported HTML creations from Chat or Work, check your chosen site name, and manage links, updates, expiry, and unpublishing in My Sites.',
  },
  {
    icon: 'graduation-cap',
    title: 'Learn with narrated Guides',
    body: 'Explore nine modules, practical exercises, and narrated videos covering prompts, models, media, bots, memory, Work projects, and the video studio.',
  },
  {
    icon: 'microchip',
    title: 'GPT-6.1 Sol & Claude Sonnet 5.5',
    body: 'The latest additions join the Chat and Work lineup. ElevenLabs Eleven v4 also joins Read aloud.',
  },
  {
    icon: 'list-check',
    title: 'Fewer routine Work approvals',
    body: 'Smart approval lets routine coding and recognized app or browser interactions proceed. Supported action cards also offer Approve all this session for that action type.',
  },
  {
    icon: 'robot',
    title: 'Work starts with a little company',
    body: 'See your prompt and startup status as soon as you send it, with a compact pet greeting. Desktop users can also turn on a floating companion in Appearance settings.',
  },
  {
    icon: 'magnifying-glass',
    title: 'Find the setting you need',
    body: 'Search Settings by name or everyday keywords, jump to the matching control, and explore refreshed account, appearance, credits, usage, and keyboard shortcut sections.',
  },
  {
    icon: 'mobile',
    title: 'More comfortable mobile Work',
    body: 'Enable task notifications from the conversation, use a simpler navigation menu, select part of an Android reply, and write drafts with better large-text sizing.',
  },
  {
    icon: 'folder-open',
    title: 'More control over saved files',
    body: 'General settings adds separate, password-confirmed actions for deleting uploaded files or AI-generated files. Account deletion also revokes credentials and connected access.',
  },
  {
    icon: 'map',
    title: 'Discover places with Maps',
    body: 'Start with your location or a place name, explore photos and sourced guides, and plan an outing in Chat. Suggestions can use your saved preferences, and Mac now shares the desktop Maps experience.',
  },
  {
    icon: 'thumbtack',
    title: 'Your workspace, easier to navigate',
    body: 'Reach Home, Files, Agents, Ideas, and Maps from a slim navigation rail. Keep pinned chats together, reorder collections, and find Memory in Settings.',
  },
  {
    icon: 'image',
    title: 'More ways to create images & video',
    body: 'Explore new Higgsfield-powered choices, including Wan 3.0, Kling 3.0 Turbo and 4K, HappyHorse 1.1, Recraft 4.1, and Soul 2, plus reference and editing options.',
  },
  {
    icon: 'video',
    title: 'A steadier video studio',
    body: 'Review scripts, narration, visuals, and motion with clearer approvals. Work better preserves shot instructions, recovers failed clips, and shows download progress.',
  },
  {
    icon: 'comment',
    title: 'Smoother everyday chat',
    body: 'Switch models more reliably, resize browser panes with less jumping, and reach the scrollbar beside the composer. Work suggestions focus on complex projects with several stages.',
  },
  {
    icon: 'file-code',
    title: 'More reliable Work files',
    body: 'Get revised source, data, and document files back as downloads, with stronger checks that the delivered copy includes your latest changes.',
  },
  {
    icon: 'mobile',
    title: 'A refreshed mobile workspace',
    body: 'Find search and Settings together in the sidebar header, start with a labeled New Chat button, and keep long drafts above the keyboard.',
  },
  {
    icon: 'chart-line',
    title: 'Based Benchmark',
    body: 'Compare Arya and other AI models on a shared political compass, then explore the questions, selected answers, and explanations behind each result.',
  },
  {
    icon: 'comment',
    title: 'Work questions that stay accessible',
    body: 'Return to unanswered questions with Answer now, keep draft answers through a refresh, and let required questions pause the work that depends on them.',
  },
  {
    icon: 'code',
    title: 'Fractional API credits',
    body: 'Developer text requests with reliable usage are billed in fractional credits, with more precise amounts carried through usage records and refunds.',
  },
  {
    icon: 'microchip',
    title: 'New flagship & fast models',
    body: 'Explore GPT-6 Sol, GPT-6 Luna, Claude Opus 5.5, and MiMo V2.6 Flash and Pro, with clearer model details and pricing.',
  },
  {
    icon: 'lightbulb',
    title: 'Personalized Ideas',
    body: 'Find a starting point for Chat or Work, with 36 more prompts and simpler cards for everyday questions and larger projects.',
  },
  {
    icon: 'user',
    title: 'Your profile & response style',
    body: 'Save your preferences, choose how Gab AI responds, and manage memory from Personalization settings.',
  },
  {
    icon: 'list-check',
    title: 'Interactive everyday plans',
    body: 'Use interactive meal plans, itineraries, study guides, checklists, comparisons, and event plans directly in chat.',
  },
  {
    icon: 'puzzle-piece',
    title: 'Plugins & connected tools',
    body: 'Browse Work plugins in Settings. Mac 1.0.7 fixes opening plugin details and the form for adding a custom HTTPS MCP server.',
  },
  {
    icon: 'pen-to-square',
    title: 'Revise existing Office files',
    body: 'Ask Work for targeted Word and PowerPoint edits, with better preservation of the original document and slide layout.',
  },
  {
    icon: 'gift',
    title: 'Referral rewards',
    body: 'Share a personalized invitation, track milestones, and help eligible friends start with welcome credits.',
  },
  {
    icon: 'clock',
    title: 'Simpler scheduled tasks',
    body: 'Create and edit recurring work in focused sections, and browse a separate template library that loads as you scroll.',
  },
  {
    icon: 'briefcase',
    title: 'Work mode',
    body: 'Give Gab AI a job and follow the plan as it researches, writes, codes, and creates finished files. Choose Fast, Standard, or High thinking power and keep refining in the same conversation.',
  },
  {
    icon: 'desktop',
    title: 'Gab AI for Mac',
    body: 'Work with the project folders, browser tabs, and supported apps you approve on your Mac — with native menus, task notifications, and in-app updates.',
  },
  {
    icon: 'folder-open',
    title: 'Local projects & Git',
    body: 'Browse and edit project text files, review Git diffs, run approved tests and commands, and manage development servers. Review recorded file changes and restore supported saved edits.',
  },
  {
    icon: 'mobile',
    title: 'Remote from your phone',
    body: 'Pair the iOS or Android app with your Mac to work with shared project folders and hand tasks between devices. Keep your Mac awake, online, and running Gab AI.',
  },
  {
    icon: 'globe',
    title: 'Browser extension',
    body: 'Chat beside a webpage in Chrome or Brave and add page text or a selection to your draft. Install from the Chrome Web Store, then connect a compatible Mac app for approved browser tasks.',
  },
  {
    icon: 'robot',
    title: 'Build a bot by chatting',
    body: 'Describe the helper you need, refine its instructions in conversation, and give it a model and avatar. Start a quick chat, assign a job, or schedule it to run again.',
  },
  {
    icon: 'video',
    title: 'Work video studio',
    body: 'Build a film from a script, stills, clips, voiceover, and music — or edit footage you upload. Review the stages, ask for changes, and approve the final assembly.',
  },
  {
    icon: 'code',
    title: 'Playable games & web previews',
    body: 'Ask Work to build a game or webpage, try it in the preview, and select an element or highlight a region to request a change.',
  },
  {
    icon: 'list-check',
    title: 'Goals that keep Work going',
    body: 'Start with /goal to keep an objective active across replies. See progress and elapsed time, edit the objective, or pause and resume when you need to.',
  },
  {
    icon: 'download',
    title: 'Every output in one place',
    body: 'Open a conversation’s generated files together, preview individual results, or download the collection as a ZIP. Documents, slides, spreadsheets, images, and media stay with the chat.',
  },
  {
    icon: 'table',
    title: 'Bigger, better spreadsheets',
    body: 'Work with larger sheets in a smoother editor, keep merged cells intact, and get better formula, chart, and Excel export support.',
  },
  {
    icon: 'credit-card',
    title: 'Clearer Work spending',
    body: 'Ask Work how many credits the conversation has used, or review per-task usage in Settings. Completed replies have a cleaner header, while spending checkpoints remain available.',
  },
  {
    icon: 'magnifying-glass',
    title: 'Voice web search',
    body: 'Ask in Voice Mode and webpage results appear on screen while Arya summarizes them — no Search toggle, just talk.',
  },
  {
    icon: 'folder-tree',
    title: 'Nested collection folders',
    body: 'Collections nest inside collections, so school, client, and project work can sit in a real folder tree instead of a flat list.',
  },
  {
    icon: 'layer-group',
    title: 'Collection memory',
    body: 'Give a collection its own isolated memory — facts that apply only there, not across your whole account — or turn memory off for that folder.',
  },
  {
    icon: 'microchip',
    title: 'GLM 5.3 & GLM 5.3 Flash',
    body: 'Z.ai\'s flagship for software engineering and long-horizon agents, plus a fast multimodal Flash variant — both with a 1M context window and always-on reasoning.',
  },
  {
    icon: 'credit-card',
    title: 'Auto top-up with 10% extra credits',
    body: 'Eligible credit buyers can choose a refill threshold and monthly spending limit. Automatic refills now include 10% extra credits at the same pack price.',
  },
  {
    icon: 'code',
    title: 'API Auto routing',
    body: 'The developer API now accepts model=auto and selects a chat model for your request. Embeddings landed too.',
  },
  {
    icon: 'mobile',
    title: 'Push when jobs finish',
    body: 'Android now notifies you when a media generation or scheduled task finishes, so you don\'t have to sit on the screen and wait.',
  },
  {
    icon: 'file-lines',
    title: 'Long pastes & surviving drafts',
    body: 'Paste longer documents and keep your working drafts available as the conversation grows.',
  },
  {
    icon: 'music',
    title: 'MiniMax Music 3',
    body: 'Describe a song and get a finished track — full-length pieces up to five minutes, with sectioned lyrics, instrumentals, and 44.1 kHz stereo.',
  },
  {
    icon: 'upload',
    title: '50MB uploads',
    body: 'Attach longer audio and bigger documents — the upload cap moved from ~20MB to 50MB so transcription and file work stop hitting the wall.',
  },
  {
    icon: 'church',
    title: 'Church & ministry tools',
    body: 'Eight new helpers for announcements, newsletters, events, volunteers, outreach, donor copy, small-group guides, and policy drafts — plus an AI business-card generator.',
  },
  {
    icon: 'graduation-cap',
    title: 'Rewritten Learn courses',
    body: 'The Learn path was rebuilt from the ground up, including a new lesson on generating music — so getting started matches how the product actually works now.',
  },
  {
    icon: 'mobile',
    title: 'Smoother mobile web',
    body: 'Chat on a phone browser no longer jumps when the URL bar collapses, and reading a long answer stays put instead of yanking the viewport.',
  },
  {
    icon: 'location-dot',
    title: 'Maps for local searches',
    body: 'Questions about nearby places answer with a live map — tap a pin for hours and ratings, then get directions, call, or open the website without leaving the chat.',
  },
  {
    icon: 'crown',
    title: 'Plus Premium',
    body: 'A new web-only tier for power users — 12,000 credits a month, higher API limits, and priority support at $99/mo or $999/yr.',
  },
  {
    icon: 'pen-to-square',
    title: 'Editable files in chat',
    body: 'Attach a document, ask Gab to revise it, and download the edited result — read, write, and zip workflows right inside the conversation.',
  },
  {
    icon: 'microphone',
    title: 'Voice Mode for Plus',
    body: 'Voice Mode is now a Plus feature at 35 credits per minute — talk hands-free without burning through a free balance.',
  },
  {
    icon: 'bag-shopping',
    title: 'Product search cards',
    body: 'Shopping questions now answer themselves with rich product cards — image, price, and source at a glance, right inside the conversation.',
  },
  {
    icon: 'chart-line',
    title: 'Live stock quotes',
    body: 'Ask about a ticker and get a clean quote card with the latest price and the day\'s move — no tab-switching to a finance site.',
  },
  {
    icon: 'code-branch',
    title: 'Conversation forking',
    body: 'Fork any chat to explore a different direction — a new tone, a new plan, a what-if — without losing the original thread.',
  },
  {
    icon: 'chart-pie',
    title: 'Inline charts',
    body: 'Ask for numbers, comparisons, or trends and get clean charts rendered directly in the reply instead of a wall of figures.',
  },
  {
    icon: 'arrows-rotate',
    title: 'Try another model',
    body: 'Not quite the look you wanted? Image and video generations now offer one-click suggestions to re-roll with a different model.',
  },
  {
    icon: 'volume-high',
    title: 'Your preferred Read aloud voice',
    body: 'Choose a female or male voice and keep it for next time. Read aloud starts automatically with your saved choice when you open it.',
  },
  {
    icon: 'mobile',
    title: 'Faster mobile chat',
    body: 'A big reliability pass on iOS and Android: honest errors, sends that survive a flaky connection, and noticeably smoother streaming.',
  },
];

const MODEL_GROUPS = [
  {
    icon: 'microchip',
    label: 'Chat & reasoning',
    models: ['Mistral Large 4', 'GPT-6.1 Sol', 'Claude Sonnet 5.5', 'Claude Opus 5.5', 'GPT-6 Sol', 'GPT-6 Luna', 'MiMo V2.6 Flash', 'MiMo V2.6 Pro', 'DeepSeek V4.1 Flash', 'GPT-6 Astra', 'Fable 5.1', 'Gemini 3.8 Flash', 'GLM 5.3 Flash', 'GLM 5.3', 'Ox Alpha', 'Gemini 3.7 Flash', 'DeepSeek V4 Pro', 'Muse Spark 1.2', 'Kimi K3', 'Gemini 3.6 Flash', 'Gemini 3.5 Flash Lite', 'Qwen 3.7 Flash', 'Qwen 3.8 Max', 'DeepSeek V4 Flash', 'New GPT & Claude releases'],
  },
  {
    icon: 'image',
    label: 'Image',
    models: ['Qwen Image 3 & Edit', 'Recraft 4.1 & Pro', 'Higgsfield Soul & Soul 2', 'Z-Image Turbo', 'GPT Image 2.5 Flare', 'GPT Image 2.5 Sunburst', 'Qwen Image 3 Pro', 'Flux 2 Pro', 'Ideogram v4'],
  },
  {
    icon: 'video',
    label: 'Video',
    models: ['Wan 3.0 & Prime', 'Kling 3.0 Turbo & 4K', 'Kling O3 & Omni', 'HappyHorse 1.1', 'Seedance 2.5 Edit & Extend', 'LTX 2.5', 'PixVerse v6', 'Higgsfield Cinema Studio 4.0', 'Genjutsu Motion Transfer & Object Swap', 'MiniMax H3 Max', 'FLUX.3 Video', 'Hailuo 3'],
  },
  {
    icon: 'music',
    label: 'Voice & music',
    models: ['ElevenLabs Eleven v4 TTS', 'ElevenLabs Scribe V2', 'MiniMax Music 3'],
  },
];

const TIMELINE = [
  {
    date: 'Oct 8',
    title: 'Windows project Work runs on your PC',
    body: 'Recent Windows app candidates let you work with connected project folders on your PC. Review progress, plans and file changes, attach text or images, and approve commands. Local chats stay saved on your PC, while existing cloud chats keep their history.',
  },
  {
    date: 'Oct 8',
    title: 'More tools for Windows local Work',
    body: 'The updated Windows candidate adds research, document creation, saved file downloads, and a dedicated browser. Review image creation and editing costs before approval, and choose whether to enable memory and selected plugins for each chat. Availability depends on the Windows app release.',
  },
  {
    date: 'Oct 8',
    title: 'Quicker task pickup & clearer recovery',
    body: 'Desktop and Remote tasks start more promptly, and Work offers clearer recovery messages when a request or Windows connection fails.',
  },
  {
    date: 'Oct 7',
    title: 'Separate instructions for Chat & Work',
    body: 'Personalization now lets you save separate custom instructions for Chat and the Work agent, so everyday replies and project work can follow different preferences.',
  },
  {
    date: 'Oct 7',
    title: 'Recurring payments in billing history',
    body: 'Billing history includes recurring subscription payments and receipts. Monthly Plus credits reset at midnight Eastern, with safeguards that preserve eligible balances until the rollover.',
  },
  {
    date: 'Oct 7',
    title: 'Learn joins Guides',
    body: 'Find tutorials and practical lessons together in Guides. Existing Learn links redirect to their matching guide, keeping saved lesson links useful.',
  },
  {
    date: 'Oct 7',
    title: 'New models & clearer model selection',
    body: 'Mistral Large 4 joins the model catalog. Chat better preserves your selected model and response preferences, with web search enabled by default for supported third-party text models.',
  },
  {
    date: 'Oct 6',
    title: 'Better image framing & fallback',
    body: 'Image requests better honor your requested framing, recover more reliably from generation failures, and show clearer credit estimates.',
  },
  {
    date: 'Oct 6',
    title: 'Steadier chat, browser & Work progress',
    body: 'Chat better preserves your reading position during layout changes and recovers incomplete Arya streams without losing received text. Deliberately stopping a reply avoids misleading failure alerts. Work retains approvals and inspection progress through interruptions, and native browser retries clear stale errors.',
  },
  {
    date: 'Oct 5',
    title: 'Mac 1.1.8 is available',
    body: 'The website edition of Gab AI for Mac advances to version 1.1.8, build 170, with an updated direct download. Recent Work fixes improve task approval recovery and keep launch attachments available on their original turn.',
  },
  {
    date: 'Oct 5',
    title: 'A smoother Windows workspace',
    body: 'Recent Windows app changes add docked local project editors, read-only Git review, retained command history, and protection for editor drafts when files change elsewhere. Word documents gain previews in Files, and bot cards adapt better to different window sizes.',
  },
  {
    date: 'Sep 30',
    title: 'Gab AI Business: your team, your usual account',
    body: 'Gab AI Business brings Plus and Plus Premium seats, pooled company credits, and team administration to Gab AI. Members use their existing account and normal Chat, Work, media, and API features, with company funding and private chats and files. Setup and billing live on the website, and the Business page shows enrollment availability.',
  },
  {
    date: 'Sep 30',
    title: 'Smart approval with fewer routine interruptions',
    body: 'Smart approval now lets routine project edits, local Git commits, sandboxed checks and development servers, and recognized app or browser interactions proceed within your existing access. Supported approval cards offer Approve all this session for the same action type. Publishing, purchases, deletion, sensitive changes, and uncertain interactions still receive the appropriate review.',
  },
  {
    date: 'Sep 30',
    title: 'Turn on Work notifications from your phone',
    body: 'Mobile Work conversations offer a dismissible reminder to enable notifications when an agent finishes or needs your attention. Turn on notifications opens the permission flow; if access was previously blocked, Open phone settings takes you to the system controls. The reminder stays out of the way once alerts are enabled.',
  },
  {
    date: 'Sep 30',
    title: 'Better large-text layouts & Android uploads',
    body: 'Mobile text and composer sizing gain safeguards for larger accessibility text settings, empty drafts, and invalid height measurements. Multiline prompts better preserve room for the keyboard and Send button. Android attachment preparation also handles file-picker results and retry failures more reliably.',
  },
  {
    date: 'Sep 30',
    title: 'Gab AI Business: discount codes & accurate credit recovery',
    body: 'Company seat checkout supports discount codes with a review of the discounted payment, recurring price, and price after a limited discount ends. Members can review pending usage, and credit recovery is more reliable after cancellations or failed requests.',
  },
  {
    date: 'Sep 30',
    title: 'Remote handoffs & help moving between accounts',
    body: 'Moving tasks between devices is more reliable. Help also explains how to move supported chat history between separate accounts using export and import.',
  },
  {
    date: 'Sep 29',
    title: 'GPT-6.1 Sol joins the model lineup',
    body: 'GPT-6.1 Sol is now included in the model catalog, alongside the recently added Claude Sonnet 5.5. The generated inventory, model details, and available choices have been refreshed to match the new additions.',
  },
  {
    date: 'Sep 29',
    title: 'Gab AI Business: invitations, limits & usage reports',
    body: 'Owners and admins can manage members and invitations, with delivery status and resend recovery. Owners can set member spending limits and review requests for more credits. Usage reports show feature and model attribution, member summaries, date filters, pending and refunded credits, and CSV export. Members see their own usage, and Business controls follow the selected app theme.',
  },
  {
    date: 'Sep 29',
    title: 'Gab AI Business: keep your existing owner plan',
    body: 'An eligible active Plus or Plus Premium subscription can cover the owner seat when you add at least one company seat. The original subscription keeps its billing, while its remaining included credits and future allowances join the company pool on their existing refresh schedule. Purchased personal credits stay personal. Guided setup reviews the seat mix and company bill before payment.',
  },
  {
    date: 'Sep 29',
    title: 'Mac 1.1.2 is available',
    body: 'The direct-download Mac edition advances to 1.1.2 (164), available through the website installer and in-app updater. Recent releases improve sign-in and reconnection, clarify local access and Remote pairing, and show the same app version in About and Settings → Help.',
  },
  {
    date: 'Sep 29',
    title: 'The desktop Maps experience comes to Mac',
    body: 'Mac Maps now uses the desktop discovery experience, including the map, place details, guides, and outing actions. Shared loading states and guide controls adapt to the Mac layout, bringing the web and Mac flows closer together.',
  },
  {
    date: 'Sep 29',
    title: 'Try another image model & clearer media requests',
    body: 'Rejected image tools can offer a one-click retry with a supported alternate model. Chat better recognizes song requests, explicit music lengths, and image-to-video follow-ups, and video generation follows a requested aspect ratio and duration. Ambiguous repeats without earlier media context no longer guess a paid generation.',
  },
  {
    date: 'Sep 29',
    title: 'Separate cleanup for uploads & generated files',
    body: 'General settings adds separate bulk-deletion controls for uploaded files and AI-generated files, with password confirmation. Account deletion and access cleanup are more reliable.',
  },
  {
    date: 'Sep 29',
    title: 'Faster Work conversations & task recovery',
    body: 'Work shows clearer errors and recovers interrupted tasks more reliably. Active conversations open faster, activity timers remain accurate, and pending uploads open from file links. Download links are keyboard accessible, and narrow desktop windows retain Enter-to-send.',
  },
  {
    date: 'Sep 29',
    title: 'More faithful video timing & approval quotes',
    body: 'Work video projects better follow your requested timing and preserve your revisions. Review clearer cost estimates and open teaser projects in the video studio.',
  },
  {
    date: 'Sep 29',
    title: 'Smoother HTML previews & site publication',
    body: 'HTML creations have more reliable interactive previews and publishing. Progress cards make unfinished creations clearer, and previews stay with the correct file.',
  },
  {
    date: 'Sep 29',
    title: 'Faster uploads & steadier sign-in',
    body: 'Files upload faster, Google sign-in is more reliable, and Chat recovers better from temporary connection problems. Work retains files and task progress more reliably through interruptions.',
  },
  {
    date: 'Sep 28',
    title: 'Publish websites directly from Chat & Work',
    body: 'Publish supported HTML pages and games from their saved creation or ask Work to publish them. Review the site address, check name availability, and confirm replacement of a site you already own. Publishing controls explain plan limits and offer actionable recovery when a name, entitlement, or upload needs attention.',
  },
  {
    date: 'Sep 28',
    title: 'My Sites: links, updates & sharing cards',
    body: 'My Sites brings published creations together with their live links, status, and expiry. Open a site, update or unpublish it, and find publishing help from the page. Work transcripts retain publication cards, and supported sites include social previews with a title, description, and image. The layout also fits narrow phone screens.',
  },
  {
    date: 'Sep 28',
    title: 'Gab AI Business: flexible seats & company billing',
    body: 'Mix Plus and Plus Premium seats at their individual monthly prices, buy company credit packs, and review prorated additions or upgrades on the existing renewal date. Schedule seat reductions and downgrades for renewal, or cancel future renewal. Website billing offers clearer payment recovery; store subscriptions keep their separate billing.',
  },
  {
    date: 'Sep 28',
    title: 'Claude Sonnet 5.5 & ElevenLabs Eleven v4',
    body: 'Claude Sonnet 5.5 joins Chat and Work. ElevenLabs Eleven v4 joins Read aloud, with smoother playback and your saved female or male voice preference.',
  },
  {
    date: 'Sep 28',
    title: 'Simpler mobile navigation, saved bots & favorite models',
    body: 'The mobile rail focuses on four shortcuts and a More menu, with Help beneath Credits. Saved bots appear above Your Chats, followed by favorite models. Bots, catalogs, libraries, and Business settings better follow every app theme, and opening Settings or Help can return you to a fresh chat.',
  },
  {
    date: 'Sep 28',
    title: 'Select part of an Android reply & steadier Voice Mode',
    body: 'Android users can select a portion of a chat response instead of copying the whole message. Voice cues no longer take microphone audio focus. Chat also better preserves your reading position when earlier messages change height or delayed updates arrive, and short mobile messages stay clear of the floating toolbar.',
  },
  {
    date: 'Sep 28',
    title: 'A clearer start for memory, bots & voice',
    body: 'Empty Memory settings offers guided setup and import, and Get Started links directly to those controls. Memory setup, imports, bot creation, Voice Mode onboarding, and credit-recovery dialogs use calmer, neutral layouts. Personalization save controls stay visible, and the introductory checklist can be dismissed persistently.',
  },
  {
    date: 'Sep 28',
    title: 'Emoji reactions & sharing rewards in Referrals',
    body: 'Chat can add automatic emoji reactions to your prompts, with an on-by-default preference in General settings that you can turn off. Gab and X sharing rewards now live in Referrals, with editable draft suggestions and referral links. Eligible verified posts earn 150 credits, and X verification better recognizes referral preview cards.',
  },
  {
    date: 'Sep 27',
    title: 'Learn with narrated videos & practical projects',
    body: 'The Learn hub offers directly browsable lessons, guided demonstrations, and saved exercises across nine modules. Narrated tutorials cover prompting, models, research, images, music, Voice Mode, bots, memory, documents, slides, spreadsheets, Work, and Mac access. Docs gains real product screenshots and visual guides. Videos include captions, transcripts, and an Up next handoff, and pause when you leave the page.',
  },
  {
    date: 'Sep 27',
    title: 'Immediate starts, pet greetings & an optional desktop companion',
    body: 'Chat and Work acknowledge new prompts immediately. Work shows startup progress in the conversation with a compact pet and a greeting suited to the request. Desktop users can enable a floating pet from Appearance settings; it starts off by default and uses the companion you choose.',
  },
  {
    date: 'Sep 27',
    title: 'A dedicated game-building workflow',
    body: 'Ask Work to build a playable game, review its progress, and try the result in the preview. Game projects better follow your creative brief and recover from broken controls or playback problems.',
  },
  {
    date: 'Sep 27',
    title: 'Share chats with photos & files',
    body: 'Chat sharing has a refreshed layout and supports photos and files alongside the conversation. Sharing prompts and verification have clearer correction feedback, while suggested posts can reflect a broader range of what you have created.',
  },
  {
    date: 'Sep 27',
    title: 'Test plugin connections & reconnect your browser',
    body: 'Plugin settings adds direct MCP connection tests, and TradingView connections can use browser OAuth. Mac browser setup brings live connection status into one window, preserves approved access, and explains when the extension needs reconnecting. Browser tasks also handle tall-page scrolling and stalled viewer capacity more reliably.',
  },
  {
    date: 'Sep 27',
    title: 'A narrated product tour & a new About video',
    body: 'The landing page adds a narrated Meet Gab AI tour using real product screens, with a composer below it and a path into Learn. About gains a narrated vision video. Tutorial players have designed thumbnails, clear play controls, caption controls on touch screens, and useful actions at the end.',
  },
  {
    date: 'Sep 26',
    title: '250 welcome credits & an easier first conversation',
    body: 'New accounts start with 250 welcome credits. Signup adds accessible inputs, password visibility controls, and recoverable security checks, while contextual sign-in preserves your draft. Get Started becomes a practical tour that updates after your first reply and prepares editable example prompts. Guest navigation and feature-specific upgrade screens explain the next step more clearly.',
  },
  {
    date: 'Sep 26',
    title: 'More reliable documents, calculations & revisions',
    body: 'Work delivers more consistent documents, spreadsheets, and presentations after revisions. Spreadsheet calculations, slide content, hyperlinks, PDF previews, and exports are more reliable. Office previews explain when to open the original file in another app.',
  },
  {
    date: 'Sep 26',
    title: 'Work respects pauses, permissions & finished answers',
    body: 'Work better follows your pauses, permissions, and follow-up instructions. Completed explanations avoid unnecessary downloads, and project activity remains easier to review.',
  },
  {
    date: 'Sep 26',
    title: 'Scheduled tasks keep their timezone',
    body: 'Editing a scheduled task now preserves its timezone, with improved daylight-saving calculations. Task template setup follows the selected route, and deleted scheduled Work updates the visible counts more reliably.',
  },
  {
    date: 'Sep 26',
    title: 'Help, saved tools & clearer everyday Chat',
    body: 'Help adds Learn and one-click Arya support conversations, with refreshed product guidance. Saved tools are easier to find and tool history is simpler to browse. Generated chat titles preserve your renames, JSON-only requests avoid unwanted Markdown wrappers, and search markup is less likely to leak into replies. Work suggestions focus on complex projects for Plus users.',
  },
  {
    date: 'Sep 25',
    title: 'Maps starts with where you want to go',
    body: 'Maps discovery now starts with your location or a place you enter, across mobile and desktop. The flow is simpler, place photos load without an unnecessary lookup delay, and calm loading placeholders replace unfinished content while results arrive.',
  },
  {
    date: 'Sep 25',
    title: 'Search Settings & explore refreshed controls',
    body: 'Settings search understands everyday keywords and shows direct links to matching controls. Appearance adds theme previews and grouped choices; Account, General, Credits, Usage, and keyboard shortcuts gain focused layouts. Credit balances remain visible in the sidebar, usage history loads faster, and native-app downloads replace older PWA setup instructions.',
  },
  {
    date: 'Sep 25',
    title: 'Mac 1.0.9 & 1.0.11, plus clearer editing controls',
    body: 'The direct Mac releases advanced through 1.0.9 (157) and 1.0.11 (159). Recent improvements stabilize composer text while typing, refine the goal editor and status controls, and improve sidebar headings and alignment. Browser access and reconnect setup are clearer, and streaming Work updates stay above the composer dock.',
  },
  {
    date: 'Sep 25',
    title: 'Clearer credits, upgrades & free-account controls',
    body: 'Free accounts can create collections from every drawer and remove favorites even at their limit. Chat can use a remaining credit balance without an unnecessary early hold, while stale balances refresh sooner. Automatic top-ups correctly appear in credit balances and allocation, their authorization is clearer, and upgrade buttons open the selected plan directly. Attachment processing reports stalled uploads and waits for PDFs to be ready.',
  },
  {
    date: 'Sep 24',
    title: 'Maps suggestions that remember your preferences',
    body: 'Maps discovery and outing prompts can use your saved account memory to suggest places that better fit your preferences. Personalization follows your memory controls, and the resulting outing keeps relevant context when it opens in Chat.',
  },
  {
    date: 'Sep 24',
    title: 'Maps: discover your next favorite place',
    body: 'Maps now has its own page for finding restaurants, coffee shops, parks, and family activities. Search a city, neighborhood, or address, or use your location in the browser. Browse results in a photo grid and interactive map, with a clearly labeled approximate area if precise positioning is unavailable.',
  },
  {
    date: 'Sep 24',
    title: 'A simpler workspace, with pinned chats & ordered collections',
    body: 'A slim navigation rail brings Home, Files, Agents, Ideas, and Maps together, with more destinations in an icon-labeled menu. Pinned chats have their own section, wider sidebars give titles more room, and collections show ten at a time with controls to load more and save a custom order. Memory moves into Settings, and Help opens directly from the sidebar.',
  },
  {
    date: 'Sep 24',
    title: 'More image & video models through Higgsfield',
    body: 'The media catalog adds 53 generation options across image, video, reference, and editing workflows. Highlights include Wan 3.0 and Prime, Kling 3.0 Turbo and 4K, HappyHorse 1.1, LTX 2.5, PixVerse v6, Recraft 4.1, Qwen Image 3, and Soul 2.',
  },
  {
    date: 'Sep 24',
    title: 'Place guides & plans for your next outing',
    body: 'Open a place for photos, hours, ratings, contact details, and a guide with linked sources. Sections cover what people say, what to order, the atmosphere, highlights, and practical tips when information is available. Copy, share, or download the guide, get directions, or use Plan an outing to start an interactive itinerary in Chat.',
  },
  {
    date: 'Sep 24',
    title: 'Clearer video quotes',
    body: 'Video quotes more accurately reflect the selected resolution and requested duration, making costs easier to review before generation.',
  },
  {
    date: 'Sep 24',
    title: 'More reliable video production, review & downloads',
    body: 'Work better preserves scripts, shot order, narration timing, and visual instructions across revisions. Review and recovery are clearer, music and narration sound more consistent, and downloads show progress.',
  },
  {
    date: 'Sep 24',
    title: 'Work keeps your latest instructions & finished files',
    body: 'Long tasks better retain follow-up instructions and complete answers as their context grows. Exported filenames open in the artifact viewer, archived outputs remain accessible, and app or browser inspection no longer triggers irrelevant file-delivery notices. Storage limits and failed review continuations have clearer recovery messages.',
  },
  {
    date: 'Sep 24',
    title: 'Fewer interruptions in everyday Chat',
    body: 'Suggestions to try Work now focus on substantial projects with several stages. Everyday questions, simple file requests, and interactive plans stay in Chat without repeated nudges. Switching models is more reliable in conversations with earlier reasoning and tool use, while keeping your visible chat history intact.',
  },
  {
    date: 'Sep 24',
    title: 'Steadier browser chat & better image previews',
    body: 'Resizing browser panes is more stable, long code blocks avoid repeated reflow, and the conversation scrollbar stays reachable beside the composer. Cached images fit the image studio canvas correctly, tablet chat headers have more room, and Android toolbars better respect light themes.',
  },
  {
    date: 'Sep 24',
    title: 'Mac text size & clearer browser controls',
    body: 'Mac Appearance settings add a saved text-size preference from 85% to 150%. Work better follows your chosen browser, reads page structure and form state, and replaces text in the built-in browser. Access prompts and failures remain visible, denied actions avoid repeated approval requests, and desktop controls expose clearer keyboard focus information.',
  },
  {
    date: 'Sep 24',
    title: 'Mac 1.0.8 & a shorter path into Chat',
    body: 'Mac 1.0.8 (156) removes the onboarding questionnaire and tour so verified accounts enter Chat directly. Email signups use the redesigned verification screen. The release is available through the Mac download and in-app updater, alongside the compatible mobile signup update.',
  },
  {
    date: 'Sep 24',
    title: 'More reliable upgrades, verification & saved collections',
    body: 'Plan upgrades and sign-in recover more reliably, and deleted collections disappear from your saved sidebar consistently.',
  },
  {
    date: 'Sep 23',
    title: 'Work delivers the files you revised',
    body: 'Edits to uploaded or pasted source code and data now receive stronger checks for downloadable delivery, including project ZIPs, dotfiles, and files without extensions. Follow-ups about a missing download preserve the verified revision, and recovered media downloads keep the latest corrected version.',
  },
  {
    date: 'Sep 23',
    title: 'A refreshed iPhone & Android workspace',
    body: 'The mobile sidebar puts search and Settings beside the Gab AI logo, adds a labeled New Chat button, and uses larger navigation text. Long prompts and suggested drafts size more reliably above the keyboard. Message actions stay visible on mobile, and chats reconnect more reliably after a session refresh.',
  },
  {
    date: 'Sep 23',
    title: 'Instant auto top-up & 10% extra credits',
    body: 'Eligible automatic refills now include 10% extra credits at the same pack price and start when settled usage takes your balance below your chosen threshold. Choose a monthly spending limit; accounts without one retain a daily refill limit. Existing authorizations keep their selected pack and spending controls.',
  },
  {
    date: 'Sep 23',
    title: 'Explore the Based Benchmark',
    body: 'The new Based Benchmark page compares Arya and other AI models using the same 62-question Political Compass test. Explore completed results on an interactive chart, select a model, and read its choices and explanations question by question.',
  },
  {
    date: 'Sep 23',
    title: 'Work questions, saved answers & clearer pauses',
    body: 'Unanswered Work questions remain available through Answer now, and draft answers survive a refresh. Required questions keep dependent work paused until you respond or give a new instruction. Optional questions can leave independent work moving, and a new message can redirect the task.',
  },
  {
    date: 'Sep 23',
    title: 'More faithful documents, slides & spreadsheets',
    body: 'Work revisions better preserve slide backgrounds, artwork, editable charts, Word contents headings, workbook structure, and unrelated cell styles. Presentation timelines have more room for step numbers, spreadsheet columns are easier to read, and formula handling has improved.',
  },
  {
    date: 'Sep 23',
    title: 'Better PDF forms, protection & redaction checks',
    body: 'Filled PDF forms better preserve their original controls and appearance. Work can verify and deliver password-protected PDFs while retaining their protection, and redaction checks now look for leftover text in unused PDF objects as well as visible page content.',
  },
  {
    date: 'Sep 23',
    title: 'Mac 1.0.7 & smoother window controls',
    body: 'Mac updates fix opening Add MCP server and plugin detail dialogs, improve window zoom and restore behavior, and make sidebar hover feedback immediate. Settings and About show the same app version, and connection settings recognize completed browser extension setup.',
  },
  {
    date: 'Sep 23',
    title: 'Read aloud with a voice you choose',
    body: 'Choose a female or male Read aloud voice and save it for future replies. Opening Read aloud starts playback with your saved choice, while switching voices updates the audio for the current message.',
  },
  {
    date: 'Sep 23',
    title: 'Simpler billing, invoices & API settings',
    body: 'Billing and API settings now use compact, grouped sections. Billing history has labeled Download invoice buttons, receipts have a cleaner layout, and saved cards show their brand and last four digits. Subscription displays better reflect discounted recurring prices and cancellation status.',
  },
  {
    date: 'Sep 23',
    title: 'More precise API billing & Work usage on request',
    body: 'Developer text requests with reliable usage now settle in fractional credits, rounded up to six decimal places, across Chat Completions, Responses, and Anthropic Messages. Work conversations show less billing clutter in completed replies; ask Work for the conversation’s usage when you need it.',
  },
  {
    date: 'Sep 23',
    title: 'Simpler model browsing & more Ideas',
    body: 'The model picker brings search, categories, favorites, and comparison into a simpler layout across desktop and mobile. Ideas adds 36 Chat and Work prompts with cleaner cards, tool cards use quieter styling, and chat pets appear in compact thinking indicators.',
  },
  {
    date: 'Sep 23',
    title: 'Video generation follows explicit requests',
    body: 'Video creation better follows your instructions, including requested trims. Advice and ideas avoid unnecessary generation, and downloads include the latest revision.',
  },
  {
    date: 'Sep 23',
    title: 'Smoother sign-in, navigation & task recovery',
    body: 'First sign-in handles delayed browser verification more reliably, navigation panes resize more predictably, and mobile web settings fit smaller screens better. Work recovers more reliably from temporary startup failures and keeps actionable credit-limit messages visible. Earn Credits leaves Settings once no tasks remain.',
  },
  {
    date: 'Sep 22',
    title: 'GPT-6 Sol, GPT-6 Luna & Claude Opus 5.5',
    body: 'GPT-6 Sol and Luna joined the model lineup, alongside Claude Opus 5.5. Browse a redesigned model details page with clearer capabilities, compact pricing indicators, and layouts that adapt to your screen. Models now opens with the newest additions first.',
  },
  {
    date: 'Sep 22',
    title: 'Revise Word documents & PowerPoint decks',
    body: 'Work can make targeted changes to existing Word documents and PowerPoint presentations, preserving more of the original file instead of rebuilding it. Presentation revisions handle grouped shapes and more accurate font measurements, while document exports gain additional checks.',
  },
  {
    date: 'Sep 22',
    title: 'Custom connections for Mac Work',
    body: 'Add your own HTTPS MCP server in Mac Settings → Plugins to give Work access to additional tools. Review the connection and authorize access before using it in a task.',
  },
  {
    date: 'Sep 22',
    title: 'Work that follows the task more closely',
    body: 'Work better follows staged requests, follow-ups, and your instructions about commands or downloadable files. Long tasks keep track of your latest instructions and recover more clearly after interruptions.',
  },
  {
    date: 'Sep 22',
    title: 'Clearer progress, stopping & file delivery',
    body: 'Stopping Work is more responsive and preserves partial progress. Opening messages survive history refreshes, unfinished jobs stay distinct from completed work, and failed file exports are reported instead of being described as saved. Coding tasks without a recognized check are marked unverified.',
  },
  {
    date: 'Sep 22',
    title: 'Cleaner chat, model browsing & task templates',
    body: 'Chat controls and the mobile model picker are easier to reach, and the sources panel stays beside a steadier conversation. Model cards show provider artwork and simpler pricing, while the task template library loads more choices as you scroll.',
  },
  {
    date: 'Sep 21',
    title: 'Personalized Ideas for Chat & Work',
    body: 'The new Ideas page offers starting points for everyday chats and larger Work projects. Browse suggestions by mode and start a conversation directly from an idea, with an expanded catalog of prompts to explore.',
  },
  {
    date: 'Sep 21',
    title: 'Personalization that stays with you',
    body: 'Save a personal profile, choose a response style, and manage memory controls from Personalization settings. Updated onboarding makes it easier to get started on web, Mac, iPhone, and Android.',
  },
  {
    date: 'Sep 21',
    title: 'Interactive cards for everyday plans',
    body: 'Chat can turn meal plans, travel itineraries, study plans, checklists, comparisons, project guides, and event plans into interactive cards. Track progress, explore follow-up actions, and export supported cards. Recipes and workouts also have a calmer reading layout.',
  },
  {
    date: 'Sep 21',
    title: 'Invite friends & track referral rewards',
    body: 'The referrals page now includes a sharing kit, personalized invitations, and milestone progress. Eligible referred users receive welcome credits at signup, and referral links carry attribution into mobile and Mac signup flows.',
  },
  {
    date: 'Sep 21',
    title: 'MiMo V2.6 Flash & Pro',
    body: 'MiMo V2.6 Flash and Pro joined the model lineup for coding, reasoning, image understanding, and tool-assisted work.',
  },
  {
    date: 'Sep 21',
    title: 'A simpler scheduled-task editor',
    body: 'Create and edit scheduled tasks in focused sections, with a redesigned native flow and actions that stay reachable around the keyboard. Task deletion now gives clearer completion feedback and removes deleted tasks from view reliably.',
  },
  {
    date: 'Sep 21',
    title: 'A refreshed Files library',
    body: 'Browse files in a cleaner library that automatically loads more results as you scroll. Collection sources are now labeled Files, with simpler upload controls and improved file interactions across screen sizes.',
  },
  {
    date: 'Sep 21',
    title: 'Paste screenshots on Mac & pick photos on iPhone',
    body: 'Paste screenshots and files into Mac composers. On iPhone, the attachment menu and photo grid can open above the keyboard, with controls that remain usable on smaller screens and with larger text.',
  },
  {
    date: 'Sep 21',
    title: 'Draft X replies from the browser extension',
    body: 'The browser extension preview can help draft replies on X. A new first-install walkthrough and compact prompt picker make it easier to start using the extension alongside the page you are reading.',
  },
  {
    date: 'Sep 21',
    title: 'Smoother checkout, sign-in & subscriptions',
    body: 'Checkout has a refreshed embedded payment form and better recovery after retries, sign-in, or delayed confirmations. Subscription renewals and cancellations are handled more reliably, duplicate cancellation emails are reduced, and browser verification errors offer clearer recovery.',
  },
  {
    date: 'Sep 21',
    title: 'More comfortable reading & Remote recovery',
    body: 'Long Android conversations scroll more reliably, centered pages respond to scrolling from their margins, and Mac text selection works across message blocks. Remote project history and streaming recover more smoothly, with clearer behavior when your Mac is disconnected.',
  },
  {
    date: 'Sep 20',
    title: 'Work plugins & connected services',
    body: 'Find Work plugins in Settings across Mac, web, and mobile. On Mac, connect supported Google, GitHub, GitLab, and Slack services through an authorization flow, then use their tools in Work with the access you approve.',
  },
  {
    date: 'Sep 20',
    title: 'Verified coding plans & project search',
    body: 'Work offers clearer coding plans, project search with file and line references, and undo for supported edits. Interrupted and archived tasks recover more reliably.',
  },
  {
    date: 'Sep 20',
    title: 'Scheduled work & a separate template library',
    body: 'Tasks now focuses on your scheduled work, with a separate library for discovering reusable templates. Memory, Tools, and Bots also gained simpler layouts and controls that appear when needed.',
  },
  {
    date: 'Sep 19',
    title: 'Git checks, AI review & publishing on Mac',
    body: 'Review repository changes, run snapshot checks, draft editable commit messages, and inspect AI review findings from the Mac app. The Git workflow also brings remote status, pull requests, and CI results together, with support for configured hooks and signing.',
  },
  {
    date: 'Sep 19',
    title: 'Import local Cursor & Codex conversations',
    body: 'In the Mac app, choose a local Cursor or Codex history folder, review detected conversations, and select the chat text to import. Imports skip existing conversations on repeat runs; they do not continuously sync chats or bring over attached files and tool results.',
  },
  {
    date: 'Sep 19',
    title: 'Mac project editor & permissions overview',
    body: 'Browse a project’s file tree, open and edit text files, and inspect repository changes inside the Mac app. A new permissions overview brings folder and app access into one place, and quotes now stay attached to the first message in a new chat.',
  },
  {
    date: 'Sep 18',
    title: 'Gab AI for Mac',
    body: 'The direct-download Mac app brings Chat and Work to your desktop, with local project folders, approved browser and app access, native menus, task notifications, and in-app updates. Setup walks you through the permissions each workflow needs.',
  },
  {
    date: 'Sep 18',
    title: 'Remote projects from iPhone & Android',
    body: 'Pair your phone with your Mac to use shared project folders, review changes, move documents, and hand a task over or back. Choose which folders are shared and whether edits are allowed; your Mac stays awake, online, and running Gab AI while local work continues.',
  },
  {
    date: 'Sep 18',
    title: 'Browser side chat & connected tabs',
    body: 'The Chrome and Brave extension adds a Gab AI side panel, Add page, and Ask Gab AI for selected text. With the Mac app connected, Work can use approved tabs with your existing sign-ins while you keep using other tabs. The extension is a manual-install preview.',
  },
  {
    date: 'Sep 18',
    title: 'Local coding, Git review & saved-edit recovery',
    body: 'Connect a Mac project folder to let Work read and edit files, run approved terminal commands and installed tests, and start or stop development servers. Review Git diffs and recorded task changes, with recovery for supported saved edits, creations, and moves.',
  },
  {
    date: 'Sep 18',
    title: 'Durable Work goals',
    body: 'Type /goal to give Work an objective that stays active across replies until it finishes or needs your attention. Track elapsed time, edit the goal, and pause or resume from the composer. Existing credit and approval controls still apply.',
  },
  {
    date: 'Sep 18',
    title: 'Smoother Mac tasks, search & approvals',
    body: 'Work activity returns when you reopen a task, search no longer shows stale results, and native Find shortcuts and multiline typing behave more reliably. Expired Mac access pauses the task with a recovery path, without repeated approval prompts.',
  },
  {
    date: 'Sep 18',
    title: 'Merged cells & sturdier document previews',
    body: 'Spreadsheet previews and edits preserve merged cells, including in the Mac app. Document transfers, PDF previews, presentation rendering, and media downloads gained better recovery when an import, source file, or export is unavailable.',
  },
  {
    date: 'Sep 15',
    title: 'Larger spreadsheets',
    body: 'Larger workbooks now open, scroll, and edit more smoothly. Spreadsheet generation and Excel export support larger sheets, alongside improved formulas and chart handling.',
  },
  {
    date: 'Sep 15',
    title: 'More reliable purchases & credit recovery',
    body: 'Subscription renewals, restored purchases, and delayed payment confirmations recover more reliably across payment providers. Cancelled or failed generation jobs also gained stronger credit recovery and protection against duplicate charges.',
  },
  {
    date: 'Sep 14',
    title: 'Attachment-aware credit previews & calmer mobile chat',
    body: 'Credit estimates account for attached files before you send. Chat stays put while replies stream, phone task forms stay above the keyboard, and the sidebar shows longer titles with extra details on desktop hover.',
  },
  {
    date: 'Sep 12',
    title: 'Conversation outputs in one place',
    body: 'Open all of a conversation’s generated files from one panel, preview the result you need, download individual files, or collect them in a ZIP. Media generation also follows more of the selected model’s voice, duration, sound-effect, and 3D controls.',
  },
  {
    date: 'Sep 12',
    title: 'Approvals and drafts that survive interruptions',
    body: 'Pending Work approvals stay available across completed turns and resumed tasks. Chat recovery preserves the last prompt when a conversation cannot be loaded, and mobile composer fixes keep your draft within reach.',
  },
  {
    date: 'Sep 11',
    title: 'Choose Chat or Work for scheduled tasks',
    body: 'Scheduled tasks can run as a chat or a Work job, with Chat as the default. Bind a task to a bot so recurring runs keep its instructions and identity.',
  },
  {
    date: 'Sep 11',
    title: 'Media fallbacks & clearer chat controls',
    body: 'When a media provider fails, automatic generation can try another available model. New Chat starts in Chat mode, Work shows your launch prompt immediately, and Memory and Tasks are easier to find together under More.',
  },
  {
    date: 'Sep 10',
    title: 'Bot mentions, model choices & a default bot',
    body: 'Mention a bot in the composer, choose the models it uses for Chat and Work, and select your default bot in Personalization. Work also gained an Auto-approve option for supported task approvals.',
  },
  {
    date: 'Sep 10',
    title: 'Personal onboarding',
    body: 'A shorter guided setup introduces Chat and Work and helps Gab AI learn your preferences. Revisit it from Personalization settings, and keep control of background memory learning from the Memory page.',
  },
  {
    date: 'Sep 10',
    title: 'DeepSeek V4.1 Flash',
    body: 'DeepSeek V4.1 Flash joined the lineup with image understanding, tool use, and adaptive reasoning.',
  },
  {
    date: 'Sep 9',
    title: 'Create bots in conversation & react to messages',
    body: 'Build a bot by describing what you need and refining its instructions in a setup chat. Saved bots offer Quick Chat or a job, and emoji reactions let you respond to individual messages.',
  },
  {
    date: 'Sep 8',
    title: 'Meet Work mode',
    body: 'Give Gab AI a job that takes more than an answer — research, documents, spreadsheets, presentations, code, and media. Work shows its plan, streams progress, asks for decisions, and delivers files in the conversation. Choose Fast, Standard, or High thinking power and continue with follow-up instructions.',
  },
  {
    date: 'Sep 8',
    title: 'A video studio inside Work',
    body: 'Turn a brief into a multi-scene film with scripts, reference stills, generated clips, voiceover, and music. Review stills and takes before animation and assembly, or upload footage for transcript-guided editing, cuts, captions, and motion graphics.',
  },
  {
    date: 'Sep 8',
    title: 'Playable games, web previews & image editing',
    body: 'Work can build playable HTML games and webpages, open code for review, and show previews beside the chat. Open generated images in an editor and ask for revisions without losing the original task.',
  },
  {
    date: 'Sep 8',
    title: 'Live browsing & side conversations',
    body: 'Follow Work’s browser activity in the conversation and take over when a site needs your input. On desktop web, select text from a reply to ask a side question while keeping your main conversation in place.',
  },
  {
    date: 'Sep 8',
    title: 'Work checkpoints & spending visibility',
    body: 'Saved checkpoints help you restore or refresh a Work task after an interruption. Spending checkpoints make larger jobs easier to review, per-task usage appears in Settings, and generated files stay attached to the turn that produced them.',
  },
  {
    date: 'Sep 8',
    title: 'GPT-6 Astra & GPT Image 2.5',
    body: 'GPT-6 Astra joined the model lineup. GPT Image 2.5 Flare and Sunburst added new choices for image generation and editing.',
  },
  {
    date: 'Sep 8',
    title: 'Better transcription, speech & audio previews',
    body: 'Transcription moved to ElevenLabs Scribe V2, and Eleven v3 joined the text-to-speech lineup. Work audio and voiceover takes play directly in the conversation, with notification sounds when a task needs attention.',
  },
  {
    date: 'Sep 8',
    title: 'Pixel companions & an editable memory overview',
    body: 'Eight pixel companions joined the workspace pets, and bots gained new illustrated avatars. You can also edit your Dream Memory overview so future background learning starts from your own wording.',
  },
  {
    date: 'Sep 2',
    title: 'Fable 5.1, faster memory & better attachment context',
    body: 'Fable 5.1 joined the chat lineup. Dream Memory began reviewing recent chats throughout the day, and image and PDF attachments stay with their original messages so follow-up questions keep the right context.',
  },
  {
    date: 'Sep 2',
    title: 'Gemini 3.8 Flash',
    body: 'Gemini 3.8 Flash joined the chat lineup with improved coding and reasoning capabilities and a 1M context window.',
  },
  {
    date: 'Aug 29',
    title: 'MiniMax H3 Max video',
    body: 'MiniMax H3 Max joined the video picker — Fal\'s post-trained Hailuo clip model with native audio, 5–15s, and 480p/768p.',
  },
  {
    date: 'Aug 27',
    title: 'Smoother Voice Mode on iOS',
    body: 'Voice Mode on iPhone no longer eats the first words of a reply, double-reads, or keeps playing after you hang up.',
  },
  {
    date: 'Aug 26',
    title: 'GLM 5.3 Flash & long-paste handling',
    body: 'GLM 5.3 Flash joined the picker for fast coding and agent work. Long pasted documents and interrupted replies are handled more reliably.',
  },
  {
    date: 'Aug 22',
    title: 'Collection memory & surviving drafts',
    body: 'Collections can keep their own separate memory, and working drafts remain available during long conversations.',
  },
  {
    date: 'Aug 21',
    title: 'Nested folders, Ox Alpha & auto top-up',
    body: 'Collections nest into folders, Ox Alpha joined the coding lineup, and repeat credit buyers can turn on auto top-up. The web app also gained a Download Gab AI menu, and Learn added an Agentic Workflows module.',
  },
  {
    date: 'Aug 20',
    title: 'API Auto routing',
    body: 'The developer API now accepts model=auto and selects a chat model for your request. Embedding models joined the API as well.',
  },
  {
    date: 'Aug 19',
    title: 'Voice web search',
    body: 'Ask in Voice Mode and webpage results appear on screen automatically while Arya speaks a short summary. Claude Haiku 4.5 is back in the picker.',
  },
  {
    date: 'Aug 18',
    title: 'GLM 5.3',
    body: 'Z.ai\'s flagship GLM 5.3 landed for software engineering and long-horizon agents — 1M context, always-on reasoning, and a stronger coding tradeoff than GLM 5.2.',
  },
  {
    date: 'Aug 17',
    title: 'Push when jobs finish',
    body: 'Android now notifies you when a media generation or scheduled task finishes, so you can leave the screen and still catch the result.',
  },
  {
    date: 'Aug 16',
    title: 'Faster Read aloud, church & business tools',
    body: 'Read aloud starts as soon as the first audio chunk is ready. Eight church and ministry tools landed — announcements, newsletters, events, volunteers, outreach, donor copy, small-group guides, and policy drafts — plus an AI business-card generator. Mobile web chat also stopped jumping when the URL bar collapses.',
  },
  {
    date: 'Aug 14',
    title: 'Learn overhaul',
    body: 'The Learn courses were rewritten to match the product as it is today, including a new music-generation lesson. Retired models left the picker.',
  },
  {
    date: 'Aug 13',
    title: 'Gemini 3.7 Flash & MiniMax Music 3',
    body: 'Gemini 3.7 Flash joined the chat lineup, and MiniMax Music 3 landed for full songs up to five minutes — structured lyrics, instrumentals, and 44.1 kHz stereo.',
  },
  {
    date: 'Aug 13',
    title: 'Larger uploads',
    body: 'File uploads went from ~20MB to 50MB, so long audio and bigger documents no longer hit a wall mid-attach.',
  },
  {
    date: 'Aug 12',
    title: 'DeepSeek V4 Pro & coding-agent API',
    body: 'DeepSeek V4 Pro 0813 joined the picker with a 1M context window, and the developer API gained agent-setup and OpenAPI catalog endpoints for coding agents.',
  },
  {
    date: 'Aug 12',
    title: 'Plus renew or upgrade',
    body: 'Plus members can renew Plus or upgrade to Premium from Billing — including Bitcoin annual — instead of being steered onto the wrong plan.',
  },
  {
    date: 'Aug 10',
    title: 'Maps for local searches',
    body: 'Ask about places nearby and the answer arrives with an interactive map — numbered pins you can tap for hours and ratings, one-tap directions, and a card for every result.',
  },
  {
    date: 'Aug 9',
    title: 'Plus Premium & editable files',
    body: 'Plus Premium landed for web power users (12k credits/mo), and chat can now read, edit, and return downloadable file changes — plus a more reliable Read aloud.',
  },
  {
    date: 'Aug 8',
    title: 'Dream Memory',
    body: 'Memory now learns as you chat. Say "remember" to save instantly, or let Gab quietly review recent conversations overnight and consolidate what matters — with a living overview on your Memory page.',
  },
  {
    date: 'Aug 7',
    title: 'Voice Mode for Plus',
    body: 'Voice Mode moved behind Plus at a clearer 35 credits per minute, so hands-free chat stays sustainable.',
  },
  {
    date: 'Aug 6',
    title: 'Muse Spark 1.2',
    body: 'Meta\'s Muse Spark 1.2 joined the chat lineup, with a cleaner signed-in library and a new Prime Agent developer guide.',
  },
  {
    date: 'Aug 5',
    title: 'Qwen Image 3 Pro & FLUX.3 Video',
    body: 'Two new media models landed — sharper image generation and editing from Qwen, plus FLUX.3 Video with keyframes and native audio.',
  },
  {
    date: 'Aug 4',
    title: 'Robot pets & this changelog',
    body: 'Eight robot companions joined the animal pets in your workspace, and this public changelog page went live.',
  },
  {
    date: 'Aug 3',
    title: 'Qwen 3.8 Max',
    body: 'Qwen 3.8 Max joined the picker alongside Hailuo 3 video tooling.',
  },
  {
    date: 'Aug 2',
    title: 'Hailuo 3 video',
    body: 'Hailuo 3 landed for video generation, with sturdier recipe and workout cards in chat.',
  },
  {
    date: 'Aug 1',
    title: 'Recipe & workout cards',
    body: 'Auto mode now answers recipe and workout requests with beautiful, structured cards — ingredients, steps, sets, and reps included.',
  },
  {
    date: 'Jul 31',
    title: 'Try-another-model suggestions',
    body: 'Media generations gained one-click suggestions to re-roll a result with a different model.',
  },
  {
    date: 'Jul 30',
    title: 'Product & stock cards in chat',
    body: 'Shopping search and live stock quotes arrived as rich cards, alongside a major mobile chat reliability pass.',
  },
  {
    date: 'Jul 29',
    title: 'Qwen 3.7 Flash',
    body: 'Another fast chat model joined the picker, and the workspace now remembers your preferred sidebar widths.',
  },
  {
    date: 'Jul 27',
    title: 'Redesigned sources panel',
    body: 'Web search results open in a cleaner, easier-to-scan sources panel.',
  },
  {
    date: 'Jul 25',
    title: 'Editable artifacts',
    body: 'Documents, presentations, and spreadsheets became living artifacts you can edit in place.',
  },
  {
    date: 'Jul 23',
    title: 'Forking, inline charts & read-aloud',
    body: 'Conversations gained forking, replies gained inline charts, and any response can now be read back to you aloud. Flux 2 Pro joined the image lineup.',
  },
  {
    date: 'Jul 21',
    title: 'Gemini 3.6 Flash & 3.5 Flash Lite',
    body: 'Two new Gemini models landed, and conversation memory got noticeably smarter.',
  },
  {
    date: 'Jul 16',
    title: 'Kimi K3',
    body: 'Kimi K3 joined the model picker.',
  },
  {
    date: 'Jul 9',
    title: 'Pets arrive',
    body: 'Desktop pets came to the workspace — alongside new GPT and Claude releases.',
  },
];

// ============================================================================
// FEATURED PET — cycles the pet's streaming animation frames, like the
// waiting indicator does while the AI works.
// ============================================================================
const FeaturedPet = ({ pet, isDark }) => {
  const [frameIndex, setFrameIndex] = useState(0);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    let mounted = true;
    AccessibilityInfo.isReduceMotionEnabled().then((value) => {
      if (mounted) setReduceMotion(value);
    });
    const subscription = AccessibilityInfo.addEventListener?.('reduceMotionChanged', setReduceMotion);
    return () => {
      mounted = false;
      subscription?.remove?.();
    };
  }, []);

  useEffect(() => {
    setFrameIndex(0);
    if (reduceMotion) return undefined;
    const interval = setInterval(() => {
      setFrameIndex((current) => current + 1);
    }, PET_FRAME_DURATION);
    return () => clearInterval(interval);
  }, [pet.id, reduceMotion]);

  const source = reduceMotion
    ? pet.image
    : pet.streamingImages[frameIndex % pet.streamingImages.length] || pet.image;

  return (
    <View
      style={{
        alignItems: 'center',
        padding: 28,
        borderRadius: 24,
        backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        borderWidth: 1,
        borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
      }}
    >
      <View
        style={{
          width: 190,
          height: 190,
          borderRadius: 28,
          backgroundColor: accentAlpha(isDark ? 0.08 : 0.05, isDark),
          borderWidth: 1,
          borderColor: accentAlpha(0.25, isDark),
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 20,
        }}
      >
        <Image
          source={source}
          style={{ width: 150, height: 150 }}
          contentFit="contain"
          transition={reduceMotion ? 0 : 80}
          accessibilityLabel={`${pet.name} the ${pet.species}, animating while the AI works`}
        />
      </View>
      <Text size="xl" weight="bold" style={{ letterSpacing: -0.3 }}>
        {pet.name}
      </Text>
      <Text size="s" color="secondary" style={{ marginTop: 4, marginBottom: 10 }}>
        {pet.species}
      </Text>
      <Text size="s" color="secondary" style={{ textAlign: 'center', lineHeight: 20, maxWidth: 260 }}>
        {pet.description}
      </Text>
    </View>
  );
};

// ============================================================================
// MOCK CHAT CARDS — scaled-down replicas of the real RecipeCard and
// WorkoutCard that chat renders, same accents and layout language.
// ============================================================================
const CardShell = ({ accent, icon, title, description, tags, meta, isDark, compact, children }) => (
  <View
    style={{
      // Never flex-grow inside a column — that compresses height and clips
      // the recipe/workout body on mobile web.
      flexGrow: compact ? 0 : 1,
      flexShrink: 0,
      flexBasis: compact ? 'auto' : 0,
      alignSelf: compact ? 'stretch' : undefined,
      minWidth: compact ? 0 : 300,
      maxWidth: 460,
      width: '100%',
      borderRadius: 14,
      borderWidth: 1,
      borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
      backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
      padding: compact ? 16 : 20,
      overflow: 'visible',
      ...(Platform.OS === 'web' && { boxShadow: '0 4px 18px rgba(0,0,0,0.10)' }),
    }}
  >
    <View style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: 14 }}>
      <View
        style={{
          width: 38,
          height: 38,
          borderRadius: 11,
          borderWidth: 1,
          borderColor: `${accent}55`,
          backgroundColor: `${accent}26`,
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: 12,
          marginTop: 2,
          flexShrink: 0,
        }}
      >
        <Icon name={icon} size={17} color={accent} />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text weight="bold" style={{ fontSize: compact ? 17 : 18, lineHeight: 24 }}>{title}</Text>
        <Text size="s" color="secondary" style={{ marginTop: 3, lineHeight: 19 }}>{description}</Text>
      </View>
    </View>

    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
      {tags.map((tag) => (
        <View
          key={tag}
          style={{
            borderWidth: 1,
            borderRadius: 999,
            paddingHorizontal: 10,
            paddingVertical: 4,
            borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
            backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
          }}
        >
          <Text size="xs" weight="medium" color="secondary">{tag}</Text>
        </View>
      ))}
    </View>

    {compact ? (
      // Mobile: inline chips so "10 min" never wraps under "Prep" in a skinny column.
      <View
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: 8,
          borderWidth: 1,
          borderRadius: 11,
          paddingVertical: 10,
          paddingHorizontal: 10,
          borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
          backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
          marginBottom: 16,
        }}
      >
        {meta.map((item) => (
          <View
            key={item.label}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 6,
              paddingVertical: 5,
              paddingHorizontal: 10,
              borderRadius: 999,
              borderWidth: 1,
              borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
              backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
            }}
          >
            <Icon name={item.icon} size={12} color={accent} />
            <Text size="xs" color="secondary">{item.label}</Text>
            <Text size="xs" weight="bold">{item.value}</Text>
          </View>
        ))}
      </View>
    ) : (
      <View
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          borderWidth: 1,
          borderRadius: 11,
          paddingVertical: 10,
          paddingHorizontal: 6,
          borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
          backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
          marginBottom: 16,
        }}
      >
        {meta.map((item) => (
          <View
            key={item.label}
            style={{
              flexGrow: 1,
              flexBasis: 64,
              alignItems: 'center',
              paddingVertical: 2,
            }}
          >
            <Icon name={item.icon} size={13} color={accent} />
            <Text size="xs" color="secondary" style={{ marginTop: 4 }}>{item.label}</Text>
            <Text size="xs" weight="bold" numberOfLines={1} style={{ marginTop: 1 }}>{item.value}</Text>
          </View>
        ))}
      </View>
    )}

    {children}
  </View>
);

const MockRecipeCard = ({ isDark, compact }) => (
  <CardShell
    accent={RECIPE_ACCENT}
    icon="utensils"
    title="Honey-Glazed Salmon Bowl"
    description="Crispy-skinned salmon over sesame rice with quick-pickled cucumber."
    tags={['Japanese-inspired', 'High-protein', 'Weeknight']}
    meta={[
      { icon: 'clock', label: 'Prep', value: '10 min' },
      { icon: 'fire', label: 'Cook', value: '15 min' },
      { icon: 'stopwatch', label: 'Total', value: '25 min' },
      { icon: 'users', label: 'Serves', value: '2' },
      { icon: 'star', label: 'Level', value: 'Easy' },
    ]}
    isDark={isDark}
    compact={compact}
  >
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 10 }}>
      <Icon name="list-check" size={13} color={RECIPE_ACCENT} />
      <Text size="s" weight="bold">Ingredients</Text>
    </View>
    {['2 salmon fillets, skin on', '1 cup jasmine rice', '2 tbsp honey', '1 tbsp soy sauce', '1 cucumber, thinly sliced'].map((ingredient) => (
      <View key={ingredient} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
        <Icon name="circle-check" size={12} color={RECIPE_ACCENT} style={{ marginTop: 3 }} />
        <Text size="s" style={{ flex: 1, lineHeight: 19 }}>{ingredient}</Text>
      </View>
    ))}
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 10, marginBottom: 10 }}>
      <Icon name="list-ol" size={13} color={RECIPE_ACCENT} />
      <Text size="s" weight="bold">Instructions</Text>
    </View>
    {['Cook the rice and pickle the cucumber in rice vinegar.', 'Sear the salmon skin-side down until crisp.', 'Glaze with honey and soy, then bowl it up.'].map((step, index) => (
      <View key={step} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
        <View
          style={{
            width: 20,
            height: 20,
            borderRadius: 10,
            borderWidth: 1,
            borderColor: `${RECIPE_ACCENT}55`,
            backgroundColor: `${RECIPE_ACCENT}26`,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Text size="xs" weight="bold" style={{ color: RECIPE_ACCENT }}>{index + 1}</Text>
        </View>
        <Text size="s" style={{ flex: 1, lineHeight: 19 }}>{step}</Text>
      </View>
    ))}
  </CardShell>
);

const MockWorkoutCard = ({ isDark, compact }) => (
  <CardShell
    accent={WORKOUT_ACCENT}
    icon="dumbbell"
    title="20-Minute Full-Body Burn"
    description="A no-equipment circuit you can do in a living room."
    tags={['No equipment', 'Beginner-friendly']}
    meta={[
      { icon: 'clock', label: 'Duration', value: '20 min' },
      { icon: 'bolt', label: 'Level', value: 'Beginner' },
      { icon: 'arrows-rotate', label: 'Rounds', value: '3' },
      { icon: 'list-ol', label: 'Exercises', value: '5' },
      { icon: 'fire', label: 'Burn', value: '~220 kcal' },
    ]}
    isDark={isDark}
    compact={compact}
  >
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 10 }}>
      <Icon name="heart-pulse" size={13} color={WORKOUT_ACCENT} />
      <Text size="s" weight="bold">Exercises</Text>
    </View>
    {[
      ['Jumping jacks', '45 sec'],
      ['Push-ups', '12 reps'],
      ['Bodyweight squats', '15 reps'],
      ['Plank', '45 sec'],
      ['Alternating lunges', '10 per leg'],
    ].map(([name, spec], index) => (
      <View key={name} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <View
          style={{
            width: 20,
            height: 20,
            borderRadius: 10,
            borderWidth: 1,
            borderColor: `${WORKOUT_ACCENT}55`,
            backgroundColor: `${WORKOUT_ACCENT}26`,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Text size="xs" weight="bold" style={{ color: WORKOUT_ACCENT }}>{index + 1}</Text>
        </View>
        <Text size="s" style={{ flex: 1 }}>{name}</Text>
        <Text size="xs" weight="medium" color="secondary">{spec}</Text>
      </View>
    ))}
  </CardShell>
);

// ============================================================================
// PLACES MOCK — the map card as it renders for local searches
// ============================================================================
const MOCK_PLACES = [
  { name: 'Drip Coffee Company', kind: 'Coffee shop', rating: '4.6', reviews: '104', status: 'Open · Closes 6 PM', open: true, address: '168 N Main St, Shavertown' },
  { name: 'Bagel Art Bakery', kind: 'Coffee shop', rating: '4.5', reviews: '263', status: 'Open · Closes 3 PM', open: true, address: '63 Memorial Hwy, Dallas' },
  { name: "KB's Coffee House", kind: 'Coffee shop', rating: '4.8', reviews: '254', status: 'Closed · Opens 8 AM', open: false, address: '206 N Main St, Shavertown' },
];

// Pin positions are hand-placed percentages — a plausible cluster, not real GPS.
const MOCK_PINS = [
  { top: '30%', left: '26%' },
  { top: '54%', left: '44%' },
  { top: '38%', left: '62%' },
  { top: '66%', left: '74%' },
];

const MapPin = ({ index, selected, top, left }) => (
  <View
    style={{
      position: 'absolute',
      top,
      left,
      width: 26,
      height: 26,
      borderRadius: 13,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 2,
      borderColor: '#ffffff',
      backgroundColor: selected ? MAP_PIN_SELECTED : MAP_PIN,
      ...(Platform.OS === 'web' && { boxShadow: '0 2px 8px rgba(0,0,0,0.35)' }),
    }}
  >
    <Text size="xxs" weight="extraBold" color="white">{index + 1}</Text>
  </View>
);

const MapActionPill = ({ icon, label, primary, isDark }) => (
  <View
    style={{
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 9,
      paddingVertical: 5,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: primary
        ? (isDark ? '#f3f4f6' : '#111827')
        : (isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.10)'),
      backgroundColor: primary
        ? (isDark ? '#f3f4f6' : '#111827')
        : (isDark ? 'rgba(255,255,255,0.04)' : '#ffffff'),
    }}
  >
    <Icon name={icon} size={10} color={primary ? (isDark ? '#111827' : '#ffffff') : (isDark ? '#e5e7eb' : '#111827')} />
    <Text size="xxs" weight="bold" style={{ color: primary ? (isDark ? '#111827' : '#ffffff') : undefined }}>
      {label}
    </Text>
  </View>
);

const MockPlacesCard = ({ isDark, compact }) => (
  <View
    style={{
      width: '100%',
      maxWidth: 620,
      borderRadius: compact ? 14 : 16,
      borderWidth: 1,
      borderColor: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)',
      backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
      overflow: 'hidden',
      ...(Platform.OS === 'web' && { boxShadow: '0 8px 28px rgba(0,0,0,0.10)' }),
    }}
  >
    {/* Map surface */}
    <View style={{ height: compact ? 180 : 230, backgroundColor: isDark ? '#20262e' : '#e9eef2', position: 'relative' }}>
      {/* Suggested roads and water, purely decorative. */}
      <View style={{ position: 'absolute', top: '22%', left: 0, right: 0, height: 4, backgroundColor: isDark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.08)' }} />
      <View style={{ position: 'absolute', top: '61%', left: 0, right: 0, height: 6, backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)' }} />
      <View style={{ position: 'absolute', top: 0, bottom: 0, left: '37%', width: 5, backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)' }} />
      <View style={{ position: 'absolute', top: 0, bottom: 0, left: '69%', width: 4, backgroundColor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)' }} />
      <View style={{ position: 'absolute', top: '40%', left: '10%', right: '30%', height: 3, backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' }} />
      <View
        style={{
          position: 'absolute',
          top: '8%',
          right: '9%',
          width: 92,
          height: 46,
          borderRadius: 24,
          backgroundColor: isDark ? 'rgba(96,165,250,0.16)' : 'rgba(147,197,253,0.45)',
        }}
      />

      {/* Zoom control */}
      <View
        style={{
          position: 'absolute',
          top: 10,
          left: 10,
          borderRadius: 9,
          overflow: 'hidden',
          borderWidth: 1,
          borderColor: 'rgba(0,0,0,0.12)',
          backgroundColor: 'rgba(255,255,255,0.97)',
          ...(Platform.OS === 'web' && { boxShadow: '0 2px 10px rgba(0,0,0,0.18)' }),
        }}
      >
        {['plus', 'minus'].map((icon, index) => (
          <View
            key={icon}
            style={{
              width: 30,
              height: 30,
              alignItems: 'center',
              justifyContent: 'center',
              borderTopWidth: index === 1 ? 1 : 0,
              borderTopColor: 'rgba(0,0,0,0.08)',
            }}
          >
            <Icon name={icon} size={11} color="#111827" />
          </View>
        ))}
      </View>

      {MOCK_PINS.map((pin, index) => (
        <MapPin key={pin.left} index={index} selected={index === 0} top={pin.top} left={pin.left} />
      ))}
    </View>

    {/* Selected-place detail bar */}
    <View
      style={{
        paddingHorizontal: 12,
        paddingVertical: 10,
        gap: 8,
        borderTopWidth: 1,
        borderTopColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <View style={{ width: 20, height: 20, borderRadius: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: MAP_PIN_SELECTED }}>
          <Text size="xxs" weight="extraBold" color="white">1</Text>
        </View>
        <Text size="s" weight="bold" numberOfLines={1} style={{ flexShrink: 1 }}>
          {MOCK_PLACES[0].name}
        </Text>
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
        <Icon name="star" size={10} color="#fbbf24" solid />
        <Text size="xs" color="secondary">{MOCK_PLACES[0].rating} ({MOCK_PLACES[0].reviews})</Text>
        <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: '#3ecf8e' }} />
        <Text size="xs" color="secondary">{MOCK_PLACES[0].status}</Text>
        {!compact && (
          <Text size="xs" color="tertiary" numberOfLines={1} style={{ flexShrink: 1 }}>
            · {MOCK_PLACES[0].address}
          </Text>
        )}
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <MapActionPill icon="route" label="Directions" primary isDark={isDark} />
        <MapActionPill icon="phone" label="Call" isDark={isDark} />
        <MapActionPill icon="globe" label="Website" isDark={isDark} />
        {!compact && <MapActionPill icon="arrow-up-right-from-square" label="Maps" isDark={isDark} />}
      </View>
    </View>

    {/* Place carousel */}
    <View style={{ flexDirection: 'row', gap: 10, paddingHorizontal: 12, paddingBottom: 12, paddingTop: 2, overflow: 'hidden' }}>
      {MOCK_PLACES.slice(0, compact ? 2 : 3).map((place, index) => (
        <View
          key={place.name}
          style={{
            flex: 1,
            minWidth: 0,
            borderRadius: 11,
            borderWidth: 1,
            borderColor: index === 0 ? MAP_PIN_SELECTED : (isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)'),
            backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
            overflow: 'hidden',
          }}
        >
          <View
            style={{
              height: 58,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
              position: 'relative',
            }}
          >
            <Icon name="mug-hot" size={16} color={isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.25)'} />
            <View
              style={{
                position: 'absolute',
                top: 6,
                left: 6,
                width: 18,
                height: 18,
                borderRadius: 9,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: index === 0 ? MAP_PIN_SELECTED : 'rgba(17,24,39,0.85)',
              }}
            >
              <Text size="xxs" weight="extraBold" color="white">{index + 1}</Text>
            </View>
          </View>
          <View style={{ padding: 8, gap: 3 }}>
            <Text size="xs" weight="bold" numberOfLines={1}>{place.name}</Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
              <Icon name="star" size={9} color="#fbbf24" solid />
              <Text size="xxs" color="secondary">{place.rating}</Text>
              <Text size="xxs" color="tertiary" numberOfLines={1}>· {place.kind}</Text>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
              <View style={{ width: 5, height: 5, borderRadius: 2.5, backgroundColor: place.open ? '#3ecf8e' : '#f07178' }} />
              <Text size="xxs" color="secondary" numberOfLines={1} style={{ flexShrink: 1 }}>{place.status}</Text>
            </View>
          </View>
        </View>
      ))}
    </View>
  </View>
);

// ============================================================================
// VOICE SEARCH MOCK — the Google-style SERP panel Voice Mode renders
// ============================================================================
const MOCK_VOICE_RESULTS = [
  {
    hostname: 'nps.gov',
    title: 'Yosemite National Park (U.S. National Park Service)',
    snippet: 'Plan your visit — hours, trails, lodging, and current conditions for Yosemite Valley and the high country.',
  },
  {
    hostname: 'alltrails.com',
    title: 'Best Hikes in Yosemite National Park',
    snippet: 'Mist Trail, Four Mile Trail, and Sentinel Dome — distances, elevation, and recent trail reports.',
  },
  {
    hostname: 'yosemite.com',
    title: 'Yosemite Valley Visitor Guide',
    snippet: 'Where to stay, when the valley is least crowded, and which viewpoints are worth the extra drive.',
  },
];

const MockVoiceSearch = ({ isDark, compact }) => (
  <View
    style={{
      width: '100%',
      maxWidth: 620,
      borderRadius: compact ? 14 : 16,
      borderWidth: 1,
      borderColor: isDark ? 'rgba(124,58,237,0.28)' : 'rgba(91,33,182,0.18)',
      backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
      overflow: 'hidden',
      ...(Platform.OS === 'web' && { boxShadow: '0 8px 28px rgba(0,0,0,0.10)' }),
    }}
  >
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingHorizontal: compact ? 14 : 16,
        paddingVertical: compact ? 12 : 14,
        borderBottomWidth: 1,
        borderBottomColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
        backgroundColor: isDark ? 'rgba(124,58,237,0.10)' : 'rgba(124,58,237,0.05)',
      }}
    >
      <View
        style={{
          width: 36,
          height: 36,
          borderRadius: 18,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: `${VOICE_SEARCH_ACCENT}22`,
          borderWidth: 1,
          borderColor: `${VOICE_SEARCH_ACCENT}55`,
        }}
      >
        <Icon name="microphone" size={15} color={VOICE_SEARCH_ACCENT} />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text size="s" weight="bold">Voice Mode</Text>
        <Text size="xs" color="secondary" numberOfLines={1}>
          {compact ? 'Summarizing web results…' : 'Talking — web results on screen while Arya answers'}
        </Text>
      </View>
      <View
        style={{
          width: 8,
          height: 8,
          borderRadius: 4,
          backgroundColor: '#ef4444',
        }}
      />
    </View>

    <View style={{ paddingHorizontal: compact ? 14 : 16, paddingTop: 12, paddingBottom: 6 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <Icon name="magnifying-glass" size={13} color={isDark ? '#a78bfa' : '#5b21b6'} />
        <Text size="s" weight="bold">Web results</Text>
      </View>
      <Text size="xs" color="secondary" style={{ marginBottom: 10 }}>
        best hikes in Yosemite this weekend
      </Text>

      {MOCK_VOICE_RESULTS.slice(0, compact ? 2 : 3).map((result) => (
        <View
          key={result.hostname}
          style={{
            paddingVertical: 10,
            paddingHorizontal: 8,
            borderRadius: 10,
            marginBottom: 2,
          }}
        >
          <Text size="xs" weight="medium" style={{ color: isDark ? '#a78bfa' : '#5b21b6', marginBottom: 2 }} numberOfLines={1}>
            {result.hostname}
          </Text>
          <Text size="s" weight="bold" style={{ marginBottom: 2, lineHeight: 18 }} numberOfLines={2}>
            {result.title}
          </Text>
          <Text size="xs" color="secondary" numberOfLines={2} style={{ lineHeight: 16 }}>
            {result.snippet}
          </Text>
        </View>
      ))}
    </View>
  </View>
);

// ============================================================================
// MUSIC MOCK — a generated-track player, same language as the About player
// ============================================================================
const MUSIC_WAVEFORM = [
  8, 14, 10, 18, 26, 16, 30, 38, 28, 42, 34, 22, 16, 26, 36, 44, 38, 30, 20, 12,
  18, 28, 34, 24, 16, 10, 16, 22, 30, 20, 12, 8, 14, 24, 32, 38, 30, 18, 12, 8,
].map((height, order) => ({ id: `wave-${order}-${height}`, height, order }));

const MockMusicCard = ({ isDark, compact }) => (
  <View
    style={{
      width: '100%',
      maxWidth: 620,
      borderRadius: compact ? 14 : 16,
      borderWidth: 1,
      borderColor: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)',
      backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
      padding: compact ? 18 : 24,
      overflow: 'hidden',
      ...(Platform.OS === 'web' && { boxShadow: '0 8px 28px rgba(0,0,0,0.10)' }),
    }}
  >
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 18 }}>
      <View
        style={{
          width: 46,
          height: 46,
          borderRadius: 13,
          alignItems: 'center',
          justifyContent: 'center',
          borderWidth: 1,
          borderColor: `${MUSIC_ACCENT}55`,
          backgroundColor: `${MUSIC_ACCENT}22`,
          flexShrink: 0,
        }}
      >
        <Icon name="music" size={18} color={MUSIC_ACCENT} />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text weight="bold" style={{ fontSize: compact ? 16 : 18, letterSpacing: -0.3 }} numberOfLines={1}>
          Westbound, After Midnight
        </Text>
        <Text size="xs" color="secondary" style={{ marginTop: 3 }}>
          MiniMax Music 3 · Indie pop · Vocals
        </Text>
      </View>
      <View
        style={{
          width: 44,
          height: 44,
          borderRadius: 22,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: MUSIC_ACCENT,
          flexShrink: 0,
        }}
      >
        <Icon name="play" size={15} color="#ffffff" solid style={{ marginLeft: 2 }} />
      </View>
    </View>

    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
        height: compact ? 36 : 44,
        marginBottom: 8,
      }}
    >
      {MUSIC_WAVEFORM.map((bar) => (
        <View
          key={bar.id}
          style={{
            flex: 1,
            height: compact ? Math.max(6, bar.height * 0.75) : bar.height,
            borderRadius: 2,
            backgroundColor: bar.order < 14
              ? MUSIC_ACCENT
              : (isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)'),
          }}
        />
      ))}
    </View>
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 16 }}>
      <Text size="xxs" color="tertiary">1:28</Text>
      <Text size="xxs" color="tertiary">4:12</Text>
    </View>

    <View
      style={{
        borderTopWidth: 1,
        borderTopColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
        paddingTop: 14,
        gap: 10,
      }}
    >
      {[
        ['Verse', 'Headlights on the two-lane, radio low, the desert still warm from the day.'],
        ['Chorus', 'Westbound, after midnight — we don\'t need a map if the sky stays this wide.'],
      ].map(([section, line]) => (
        <View key={section} style={{ gap: 3 }}>
          <Text size="xxs" weight="bold" style={{ color: MUSIC_ACCENT, letterSpacing: 1, textTransform: 'uppercase' }}>
            {section}
          </Text>
          <Text size="s" color="secondary" style={{ lineHeight: 20 }}>
            {line}
          </Text>
        </View>
      ))}
    </View>
  </View>
);

// ============================================================================
// EDITABLE ARTIFACT MOCKS — mini editor windows for docs / slides / sheets
// ============================================================================
const ArtifactWindowChrome = ({ accent, icon, filename, isDark, compact, children }) => (
  <View
    style={{
      flexGrow: compact ? 0 : 1,
      flexBasis: compact ? 'auto' : 280,
      alignSelf: 'stretch',
      minWidth: compact ? 0 : 260,
      maxWidth: compact ? '100%' : 360,
      width: '100%',
      borderRadius: compact ? 14 : 16,
      borderWidth: 1,
      borderColor: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)',
      backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
      overflow: 'hidden',
      ...(Platform.OS === 'web' && { boxShadow: '0 8px 28px rgba(0,0,0,0.10)' }),
    }}
  >
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingHorizontal: 12,
        paddingVertical: compact ? 9 : 10,
        borderBottomWidth: 1,
        borderBottomColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
        backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
      }}
    >
      {!compact && (
        <View style={{ flexDirection: 'row', gap: 5, flexShrink: 0 }}>
          {['#ff5f57', '#febc2e', '#28c840'].map((dot) => (
            <View key={dot} style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: dot }} />
          ))}
        </View>
      )}
      <View
        style={{
          width: 26,
          height: 26,
          borderRadius: 7,
          backgroundColor: `${accent}22`,
          borderWidth: 1,
          borderColor: `${accent}44`,
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon name={icon} size={12} color={accent} />
      </View>
      <Text size="xs" weight="medium" numberOfLines={1} style={{ flex: 1, minWidth: 0 }}>
        {filename}
      </Text>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
          paddingHorizontal: compact ? 7 : 8,
          paddingVertical: 3,
          borderRadius: 999,
          backgroundColor: `${accent}18`,
          borderWidth: 1,
          borderColor: `${accent}40`,
          flexShrink: 0,
        }}
      >
        <Icon name="pen" size={9} color={accent} />
        <Text size="xxs" weight="bold" style={{ color: accent }}>
          {compact ? 'Edit' : 'Editable'}
        </Text>
      </View>
    </View>
    <View style={{ padding: compact ? 14 : 16, minHeight: compact ? 0 : 200 }}>
      {children}
    </View>
  </View>
);

const MockDocumentArtifact = ({ isDark, compact }) => (
  <ArtifactWindowChrome
    accent={DOC_ACCENT}
    icon="file-word"
    filename={compact ? 'Product Brief.md' : 'Q3 Product Brief.md'}
    isDark={isDark}
    compact={compact}
  >
    <Text weight="bold" style={{ fontSize: compact ? 17 : 18, lineHeight: 24, marginBottom: 8, color: isDark ? '#f3f4f6' : '#111827' }}>
      Q3 Product Brief
    </Text>
    <Text size="s" color="secondary" style={{ lineHeight: 20, marginBottom: 14 }}>
      Ship the things people ask for — and make the answers themselves editable.
    </Text>
    <Text size="xs" weight="bold" style={{ color: DOC_ACCENT, letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 8 }}>
      Goals
    </Text>
    {['Editable docs, decks, and sheets in chat', 'Keep refining without re-prompting', 'Export anytime as .md, .pptx, or .xlsx'].map((line) => (
      <View key={line} style={{ flexDirection: 'row', gap: 8, marginBottom: 6, alignItems: 'flex-start' }}>
        <View style={{ width: 5, height: 5, borderRadius: 2.5, backgroundColor: DOC_ACCENT, marginTop: 7, flexShrink: 0 }} />
        <Text size="s" style={{ flex: 1, minWidth: 0, lineHeight: 19 }}>{line}</Text>
      </View>
    ))}
    {/* Fake caret / edit affordance */}
    <View
      style={{
        marginTop: 14,
        height: 18,
        borderLeftWidth: 2,
        borderLeftColor: DOC_ACCENT,
        paddingLeft: 8,
        justifyContent: 'center',
      }}
    >
      <Text size="xs" color="secondary" style={{ fontStyle: 'italic' }}>Keep typing to refine…</Text>
    </View>
  </ArtifactWindowChrome>
);

const MockPresentationArtifact = ({ isDark, compact }) => (
  <ArtifactWindowChrome
    accent={SLIDE_ACCENT}
    icon="file-powerpoint"
    filename={compact ? 'Update.pptx' : 'Investor Update.pptx'}
    isDark={isDark}
    compact={compact}
  >
    <View
      style={{
        flex: 1,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)',
        backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : '#fafafa',
        padding: compact ? 14 : 18,
        minHeight: compact ? 140 : 168,
        justifyContent: 'center',
      }}
    >
      <Text size="xxs" weight="bold" style={{ color: SLIDE_ACCENT, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
        Slide 1 of 6
      </Text>
      <Text weight="bold" style={{ fontSize: compact ? 18 : 20, lineHeight: 26, marginBottom: 12 }}>
        Q3 Highlights
      </Text>
      {['Revenue up 40% year over year', 'Launched editable artifacts', 'Expanded into three new markets'].map((line) => (
        <View key={line} style={{ flexDirection: 'row', gap: 8, marginBottom: 7, alignItems: 'flex-start' }}>
          <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: SLIDE_ACCENT, marginTop: 6, flexShrink: 0 }} />
          <Text size="s" style={{ flex: 1, minWidth: 0, lineHeight: 19 }}>{line}</Text>
        </View>
      ))}
    </View>
  </ArtifactWindowChrome>
);

// ============================================================================
// DREAM MEMORY MOCK — overview card + learned memories with dream badges
// ============================================================================
const DreamBadge = ({ label, accent }) => (
  <View
    style={{
      paddingHorizontal: 10,
      paddingVertical: 4,
      borderRadius: 6,
      backgroundColor: `${accent}18`,
      borderWidth: 1,
      borderColor: `${accent}45`,
    }}
  >
    <Text size="xxs" weight="bold" style={{ color: accent }}>
      {label}
    </Text>
  </View>
);

const MockDreamMemory = ({ isDark, compact }) => {
  const c = landingPalette(isDark);
  const memories = [
    {
      text: 'Prefers concise answers with concrete next steps — skip the preamble.',
      badge: 'Learned by Gab AI',
      accent: DREAM_ACCENT,
      when: 'Earlier today',
    },
    {
      text: 'Working on a Gab AI launch post; tone should stay direct and product-first.',
      badge: 'Auto-saved',
      accent: c.accent,
      when: '2 days ago',
    },
    {
      text: 'Lives in the Eastern time zone; morning workouts before deep work.',
      badge: 'Learned by Gab AI',
      accent: DREAM_ACCENT,
      when: 'This week',
    },
  ];

  return (
    <View
      style={{
        width: '100%',
        maxWidth: 640,
        borderRadius: 20,
        borderWidth: 1,
        borderColor: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)',
        backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
        overflow: 'hidden',
        ...(Platform.OS === 'web' && { boxShadow: '0 8px 28px rgba(0,0,0,0.10)' }),
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 10,
          paddingHorizontal: compact ? 16 : 20,
          paddingVertical: compact ? 14 : 16,
          borderBottomWidth: 1,
          borderBottomColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
          backgroundColor: isDark ? 'rgba(139,92,246,0.08)' : 'rgba(139,92,246,0.05)',
        }}
      >
        <View
          style={{
            width: 36,
            height: 36,
            borderRadius: 12,
            backgroundColor: `${DREAM_ACCENT}22`,
            borderWidth: 1,
            borderColor: `${DREAM_ACCENT}44`,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name="moon" size={16} color={DREAM_ACCENT} />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text size="s" weight="bold">Memory overview</Text>
          <Text size="xs" color="secondary" style={{ marginTop: 2 }}>
            Quietly updated as you chat
          </Text>
        </View>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 5,
            paddingHorizontal: 10,
            paddingVertical: 4,
            borderRadius: 999,
            backgroundColor: `${DREAM_ACCENT}18`,
            borderWidth: 1,
            borderColor: `${DREAM_ACCENT}40`,
          }}
        >
          <Icon name="sparkles" size={10} color={DREAM_ACCENT} />
          <Text size="xxs" weight="bold" style={{ color: DREAM_ACCENT }}>
            Dreaming
          </Text>
        </View>
      </View>

      <View style={{ padding: compact ? 16 : 20 }}>
        <Text size="s" color="secondary" style={{ lineHeight: 21, marginBottom: compact ? 16 : 20 }}>
          Builds product-led launch posts, prefers short actionable replies, and trains before deep work —
          facts Gab AI consolidated from recent chats.
        </Text>

        <Text
          size="xs"
          weight="bold"
          style={{
            color: DREAM_ACCENT,
            letterSpacing: 0.6,
            textTransform: 'uppercase',
            marginBottom: 10,
          }}
        >
          Recently learned
        </Text>

        {memories.map((memory, index) => (
          <View
            key={memory.text}
            style={{
              paddingVertical: 12,
              borderTopWidth: index === 0 ? 0 : 1,
              borderTopColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
            }}
          >
            <Text size="s" style={{ lineHeight: 20, marginBottom: 8 }}>
              {memory.text}
            </Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <DreamBadge label={memory.badge} accent={memory.accent} />
              <Text size="xs" color="secondary">{memory.when}</Text>
            </View>
          </View>
        ))}
      </View>
    </View>
  );
};

const MockSpreadsheetArtifact = ({ isDark, compact }) => {
  const header = ['Channel', 'Spend', 'ROI'];
  const rows = [
    ['Paid ads', '$12,400', '3.2×'],
    ['Email', '$4,100', '5.1×'],
    ['Organic', '$1,800', '7.4×'],
    ...(compact ? [] : [['Partners', '$3,250', '4.0×']]),
  ];
  const cellBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const headerBg = isDark ? 'rgba(22,163,74,0.18)' : 'rgba(22,163,74,0.10)';

  return (
    <ArtifactWindowChrome
      accent={SHEET_ACCENT}
      icon="file-excel"
      filename={compact ? 'Budget.xlsx' : 'Campaign Budget.xlsx'}
      isDark={isDark}
      compact={compact}
    >
      <View
        style={{
          borderWidth: 1,
          borderColor: cellBorder,
          borderRadius: 8,
          overflow: 'hidden',
        }}
      >
        <View style={{ flexDirection: 'row', backgroundColor: headerBg }}>
          {header.map((col) => (
            <View
              key={col}
              style={{
                flex: 1,
                minWidth: 0,
                paddingVertical: compact ? 7 : 8,
                paddingHorizontal: compact ? 6 : 8,
                borderRightWidth: col === header[header.length - 1] ? 0 : 1,
                borderRightColor: cellBorder,
              }}
            >
              <Text size="xs" weight="bold" style={{ color: SHEET_ACCENT }} numberOfLines={1}>{col}</Text>
            </View>
          ))}
        </View>
        {rows.map((row, rowIndex) => (
          <View
            key={row[0]}
            style={{
              flexDirection: 'row',
              backgroundColor: rowIndex % 2 === 0
                ? (isDark ? 'rgba(255,255,255,0.02)' : '#ffffff')
                : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'),
              borderTopWidth: 1,
              borderTopColor: cellBorder,
            }}
          >
            {row.map((cell, cellIndex) => (
              <View
                key={`${row[0]}-${cellIndex}`}
                style={{
                  flex: 1,
                  minWidth: 0,
                  paddingVertical: compact ? 7 : 8,
                  paddingHorizontal: compact ? 6 : 8,
                  borderRightWidth: cellIndex === row.length - 1 ? 0 : 1,
                  borderRightColor: cellBorder,
                }}
              >
                <Text size="xs" weight={cellIndex === 0 ? 'medium' : 'normal'} numberOfLines={1}>
                  {cell}
                </Text>
              </View>
            ))}
          </View>
        ))}
      </View>
      <Text size="xs" color="secondary" style={{ marginTop: 10 }}>
        {compact ? 'Tap a cell to edit.' : 'Click any cell to edit — changes save to the chat.'}
      </Text>
    </ArtifactWindowChrome>
  );
};

// ============================================================================
// MAIN PAGE
// ============================================================================
export default function ChangelogPage() {
  const { colors, theme } = useTheme();
  const router = useRouter();
  const { isXS, isSM } = useResponsiveness();
  const isDark = isDarkTheme(theme);
  const c = landingPalette(isDark);
  const [selectedPetId, setSelectedPetId] = useState(PETS[0].id);
  const [timelineExpanded, setTimelineExpanded] = useState(false);

  const selectedPet = useMemo(
    () => PETS.find((pet) => pet.id === selectedPetId) || PETS[0],
    [selectedPetId],
  );

  const visibleTimeline = useMemo(
    () => (timelineExpanded ? TIMELINE : TIMELINE.slice(0, TIMELINE_PREVIEW_COUNT)),
    [timelineExpanded],
  );
  const hiddenTimelineCount = TIMELINE.length - TIMELINE_PREVIEW_COUNT;

  const sectionStyle = {
    width: '100%',
    alignItems: 'center',
    paddingHorizontal: isXS ? 20 : 40,
  };

  const eyebrowStyle = {
    color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)',
    textTransform: 'uppercase',
    letterSpacing: 2,
    marginBottom: 16,
    fontSize: 13,
  };

  const cardBg = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)';
  const cardBorder = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';

  return (
    <View style={{ flex: 1, backgroundColor: colors.solid_color_primary }}>
      <FlowField isDark={isDark} />
      <MetaHead
        title="What's New — Gab AI Changelog"
        description="Gab AI updates: Windows local Work, separate Chat and Work instructions, subscription receipts, Guides, new models, Mac 1.1.8, and reliability improvements. Updated October 8, 2026."
        canonical="/changelog"
        keywords="Gab AI changelog, what's new, Gab AI Business, My Sites, website publishing, Learn tutorials, GPT-6.1 Sol, Claude Sonnet 5.5, ElevenLabs v4, Maps, Work mode, Work video studio, Gab AI for Mac, mobile chat, AI product updates"
        structuredData={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'WebPage',
              '@id': 'https://gab.ai/changelog#webpage',
              name: "What's New — Gab AI Changelog",
              description:
                'The latest Gab AI updates: Windows local Work, separate Chat and Work instructions, subscription receipts, Guides, new models, Mac 1.1.8, and reliability improvements.',
              dateModified: '2026-10-08',
              url: 'https://gab.ai/changelog',
              isPartOf: { '@id': 'https://gab.ai/#website' },
              breadcrumb: { '@id': 'https://gab.ai/changelog#breadcrumb' },
            },
            {
              '@type': 'BreadcrumbList',
              '@id': 'https://gab.ai/changelog#breadcrumb',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Gab AI', item: 'https://gab.ai/' },
                { '@type': 'ListItem', position: 2, name: "What's New", item: 'https://gab.ai/changelog' },
              ],
            },
          ],
        }}
      />

      <ScrollView
        accessibilityRole="main"
        nativeID="main-content"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ alignItems: 'center' }}
        style={{ position: 'relative', zIndex: 1 }}
      >
        {/* ================================================================ */}
        {/* HERO */}
        {/* ================================================================ */}
        <View
          style={[
            sectionStyle,
            { paddingTop: isXS ? 100 : 160, paddingBottom: isXS ? 50 : 80 },
          ]}
        >
          <View style={{ maxWidth: 860, alignItems: 'center', zIndex: 1 }}>
            <Text size="s" weight="medium" style={eyebrowStyle}>
              The Changelog · Updated October 8, 2026
            </Text>

            <Text
              tag="h1"
              weight="bold"
              style={{
                fontSize: isXS ? 32 : isSM ? 44 : 56,
                textAlign: 'center',
                marginBottom: 24,
                lineHeight: isXS ? 40 : isSM ? 52 : 64,
                letterSpacing: -1.5,
              }}
            >
              What's{' '}
              <Text weight="bold" style={{ color: c.accent, fontSize: isXS ? 32 : isSM ? 44 : 56, lineHeight: isXS ? 40 : isSM ? 52 : 64, letterSpacing: -1.5 }}>
                New
              </Text>
              {' '}at Gab AI
            </Text>

            <Text
              size="l"
              color="secondary"
              style={{
                textAlign: 'center',
                fontSize: isXS ? 16 : 18,
                lineHeight: isXS ? 26 : 30,
                maxWidth: 680,
                marginBottom: 40,
              }}
            >
              Publish your creations as websites. Learn with narrated tutorials.
              Explore Gab AI Business, new models and voices, and smoother
              Work, Maps, and mobile experiences.
            </Text>

            <View style={{ flexDirection: isXS ? 'column' : 'row', gap: 12, alignItems: 'center', width: isXS ? '100%' : 'auto' }}>
              <Button
                text="Start Chatting Free"
                variant="purple"
                size="large"
                isRound
                onPress={() => router.push('/signup')}
                style={{ minWidth: isXS ? '100%' : 220 }}
              />
              <Button
                text="Explore All Features"
                variant="secondary"
                size="large"
                isRound
                onPress={() => router.push('/features')}
                style={{ minWidth: isXS ? '100%' : 220 }}
              />
            </View>
          </View>
        </View>

        {/* ================================================================ */}
        {/* TIMELINE — the full log directly below the hero */}
        {/* ================================================================ */}
        <View style={[sectionStyle, { paddingVertical: isXS ? 60 : 100 }]}>
          <Text size="s" weight="medium" style={eyebrowStyle}>
            The Full Log
          </Text>
          <Text
            tag="h2"
            weight="bold"
            style={{ fontSize: isXS ? 28 : 36, textAlign: 'center', marginBottom: isXS ? 32 : 48, letterSpacing: -0.5 }}
          >
            Week by Week
          </Text>

          <View style={{ maxWidth: 760, width: '100%' }}>
            {visibleTimeline.map((entry, index) => (
              <View
                key={`${entry.date}-${entry.title}`}
                style={{
                  flexDirection: 'row',
                  gap: isXS ? 14 : 20,
                  paddingBottom: 28,
                }}
              >
                <View style={{ alignItems: 'center', width: 18 }}>
                  <View
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: 6,
                      backgroundColor: index === 0 ? c.accent : (isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'),
                      marginTop: 5,
                    }}
                  />
                  <View
                    style={{
                      flex: 1,
                      width: 1,
                      backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)',
                      marginTop: 6,
                    }}
                  />
                </View>
                <View style={{ flex: 1 }}>
                  <Text size="xs" weight="bold" style={{ color: c.accent, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>
                    {entry.date}
                  </Text>
                  <Text size="m" weight="medium" style={{ marginBottom: 4, letterSpacing: -0.2 }}>
                    {entry.title}
                  </Text>
                  <Text size="s" color="secondary" style={{ lineHeight: 21 }}>
                    {entry.body}
                  </Text>
                </View>
              </View>
            ))}

            {hiddenTimelineCount > 0 && (
              <View style={{ flexDirection: 'row', gap: isXS ? 14 : 20, paddingBottom: 28 }}>
                <View style={{ alignItems: 'center', width: 18 }}>
                  <View
                    style={{
                      flex: 1,
                      width: 1,
                      backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)',
                    }}
                  />
                </View>
                <Pressable
                  onPress={() => setTimelineExpanded((open) => !open)}
                  accessibilityRole="button"
                  accessibilityState={{ expanded: timelineExpanded }}
                  accessibilityLabel={
                    timelineExpanded
                      ? 'Show fewer changelog entries'
                      : `See ${hiddenTimelineCount} older changelog entries`
                  }
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                    paddingVertical: 10,
                    paddingHorizontal: 14,
                    borderRadius: 999,
                    borderWidth: 1,
                    borderColor: accentAlpha(isDark ? 0.45 : 0.35, isDark),
                    backgroundColor: accentAlpha(isDark ? 0.10 : 0.05, isDark),
                    alignSelf: 'flex-start',
                  }}
                >
                  <Icon
                    name={timelineExpanded ? 'chevron-up' : 'chevron-down'}
                    size={12}
                    color={c.accent}
                  />
                  <Text size="s" weight="bold" style={{ color: c.accent }}>
                    {timelineExpanded
                      ? 'Show less'
                      : `See ${hiddenTimelineCount} more`}
                  </Text>
                </Pressable>
              </View>
            )}

            {/* Open-ended tail — this log keeps growing as features ship. */}
            <View style={{ flexDirection: 'row', gap: isXS ? 14 : 20 }}>
              <View style={{ alignItems: 'center', width: 18 }}>
                <View
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 6,
                    borderWidth: 1.5,
                    borderStyle: 'dashed',
                    borderColor: c.accent,
                    backgroundColor: 'transparent',
                    marginTop: 5,
                  }}
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text size="xs" weight="bold" style={{ color: c.accent, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>
                  Next up
                </Text>
                <Text size="m" weight="medium" style={{ marginBottom: 4, letterSpacing: -0.2 }}>
                  More on the way
                </Text>
                <Text size="s" color="secondary" style={{ lineHeight: 21 }}>
                  We're shipping constantly — this log is updated every time something new
                  lands. Check back soon.
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* ================================================================ */}
        {/* MAJOR RELEASES — visual features after the full log */}
        <ChangelogHighlights isDark={isDark} compact={isSM} />

        {/* VOICE WEB SEARCH */}
        {/* ================================================================ */}
        <View
          style={[
            sectionStyle,
            {
              paddingVertical: isXS ? 60 : 100,
              backgroundColor: isDark ? 'rgba(124,58,237,0.05)' : 'rgba(124,58,237,0.04)',
            },
          ]}
        >
          <Text size="s" weight="medium" style={eyebrowStyle}>
            New · Voice Mode
          </Text>
          <Text
            tag="h2"
            weight="bold"
            style={{ fontSize: isXS ? 28 : 36, textAlign: 'center', marginBottom: 16, letterSpacing: -0.5 }}
          >
            Ask Out Loud, See the Sources
          </Text>
          <Text
            size="m"
            color="secondary"
            style={{ textAlign: 'center', marginBottom: isXS ? 32 : 48, maxWidth: 640, lineHeight: 24 }}
          >
            Voice Mode now pulls the web automatically. Webpage results land on
            screen while Arya speaks a short summary — tap a link, keep talking,
            no Search toggle to remember.
          </Text>

          <View style={{ width: '100%', maxWidth: 620 }}>
            <MockVoiceSearch isDark={isDark} compact={isXS} />
          </View>
        </View>

        {/* ================================================================ */}
        {/* MINIMAX MUSIC 3 */}
        {/* ================================================================ */}
        <View
          style={[
            sectionStyle,
            {
              paddingVertical: isXS ? 60 : 100,
              backgroundColor: isDark ? 'rgba(219,39,119,0.05)' : 'rgba(219,39,119,0.04)',
            },
          ]}
        >
          <Text size="s" weight="medium" style={eyebrowStyle}>
            Music
          </Text>
          <Text
            tag="h2"
            weight="bold"
            style={{ fontSize: isXS ? 28 : 36, textAlign: 'center', marginBottom: 16, letterSpacing: -0.5 }}
          >
            A Finished Song From a Sentence
          </Text>
          <Text
            size="m"
            color="secondary"
            style={{ textAlign: 'center', marginBottom: isXS ? 32 : 48, maxWidth: 640, lineHeight: 24 }}
          >
            MiniMax Music 3 writes full tracks up to five minutes — verse, chorus,
            and a 44.1 kHz stereo mix. Ask for an indie road song, a five-minute
            instrumental, or a hip-hop beat with lyrics, and the answer arrives as
            a playable track in the same chat.
          </Text>

          <View style={{ width: '100%', maxWidth: 620 }}>
            <MockMusicCard isDark={isDark} compact={isXS} />
          </View>
        </View>

        {/* ================================================================ */}
        {/* MAPS FOR LOCAL SEARCHES */}
        {/* ================================================================ */}
        <View style={[sectionStyle, { paddingVertical: isXS ? 60 : 100 }]}>
          <Text size="s" weight="medium" style={eyebrowStyle}>
            Local Search
          </Text>
          <Text
            tag="h2"
            weight="bold"
            style={{ fontSize: isXS ? 28 : 36, textAlign: 'center', marginBottom: 16, letterSpacing: -0.5 }}
          >
            Ask Where, Get a Map
          </Text>
          <Text
            size="m"
            color="secondary"
            style={{ textAlign: 'center', marginBottom: isXS ? 32 : 48, maxWidth: 640, lineHeight: 24 }}
          >
            Coffee near the campsite, a late-night pharmacy, the closest hardware store —
            local questions now answer with an interactive map. Tap a pin for hours,
            ratings, and one-tap directions, or scroll the cards below it to compare every
            spot at a glance.
          </Text>

          <View style={{ width: '100%', maxWidth: 620 }}>
            <MockPlacesCard isDark={isDark} compact={isXS} />
          </View>
        </View>

        {/* ================================================================ */}
        {/* DREAM MEMORY */}
        {/* ================================================================ */}
        <View
          style={[
            sectionStyle,
            {
              paddingVertical: isXS ? 60 : 100,
              backgroundColor: isDark ? 'rgba(139,92,246,0.04)' : 'rgba(139,92,246,0.03)',
            },
          ]}
        >
          <Text size="s" weight="medium" style={eyebrowStyle}>
            Dream Memory
          </Text>
          <Text
            tag="h2"
            weight="bold"
            style={{ fontSize: isXS ? 28 : 36, textAlign: 'center', marginBottom: 16, letterSpacing: -0.5 }}
          >
            Memory That Learns as You Chat
          </Text>
          <Text
            size="m"
            color="secondary"
            style={{ textAlign: 'center', marginBottom: isXS ? 32 : 48, maxWidth: 640, lineHeight: 24 }}
          >
            Say "remember" and it sticks instantly. Or just keep chatting — Gab AI quietly
            reviews recent conversations, consolidates what matters, and keeps a living
            overview on your Memory page. Background dreaming is included with Plus.
          </Text>

          <View style={{ width: '100%', alignItems: 'center', maxWidth: 680 }}>
            <MockDreamMemory isDark={isDark} compact={isXS} />
          </View>

          <View
            style={{
              flexDirection: isXS ? 'column' : 'row',
              gap: 16,
              marginTop: isXS ? 28 : 36,
              maxWidth: 680,
              width: '100%',
            }}
          >
            {[
              {
                icon: 'comment',
                title: 'Instant saves',
                body: 'Tell Gab to remember something and it lands on your Memory page with Undo.',
              },
              {
                icon: 'moon',
                title: 'Background dreaming',
                body: 'Plus quietly reviews recent chats throughout the day and consolidates what matters.',
              },
              {
                icon: 'sliders',
                title: 'You stay in control',
                body: 'Edit the overview or individual memories, delete what no longer applies, or turn learning off.',
              },
            ].map((item) => (
              <View
                key={item.title}
                style={{
                  flex: 1,
                  padding: 18,
                  borderRadius: 16,
                  backgroundColor: cardBg,
                  borderWidth: 1,
                  borderColor: cardBorder,
                }}
              >
                <View
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    backgroundColor: `${DREAM_ACCENT}18`,
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 12,
                  }}
                >
                  <Icon name={item.icon} size={15} color={DREAM_ACCENT} />
                </View>
                <Text size="s" weight="bold" style={{ marginBottom: 6 }}>{item.title}</Text>
                <Text size="xs" color="secondary" style={{ lineHeight: 18 }}>{item.body}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* ================================================================ */}
        {/* PETS */}
        {/* ================================================================ */}
        <View style={[sectionStyle, { paddingVertical: isXS ? 60 : 100 }]}>
          <Text size="s" weight="medium" style={eyebrowStyle}>
            Pets
          </Text>
          <Text
            tag="h2"
            weight="bold"
            style={{ fontSize: isXS ? 28 : 36, textAlign: 'center', marginBottom: 16, letterSpacing: -0.5 }}
          >
            Meet Your Chat Companions
          </Text>
          <Text
            size="m"
            color="secondary"
            style={{ textAlign: 'center', marginBottom: isXS ? 32 : 48, maxWidth: 640, lineHeight: 24 }}
          >
            Pick a companion to appear in a compact indicator while the AI thinks.
            Choose from animals, robots, and pixel companions — each with its
            own personality.
          </Text>

          <View
            style={{
              flexDirection: isXS ? 'column' : 'row',
              gap: isXS ? 24 : 32,
              alignItems: 'center',
              justifyContent: 'center',
              maxWidth: 1100,
              width: '100%',
            }}
          >
            <FeaturedPet pet={selectedPet} isDark={isDark} />

            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 10,
                justifyContent: 'center',
                maxWidth: 560,
                flex: 1,
              }}
            >
              {PETS.map((pet) => {
                const active = pet.id === selectedPetId;
                return (
                  <Pressable
                    key={pet.id}
                    onPress={() => setSelectedPetId(pet.id)}
                    accessibilityRole="button"
                    accessibilityState={{ selected: active }}
                    accessibilityLabel={`Feature ${pet.name} the ${pet.species}`}
                    style={{
                      width: isXS ? '22%' : 104,
                      paddingVertical: 12,
                      paddingHorizontal: 6,
                      borderRadius: 16,
                      borderWidth: active ? 1.5 : 1,
                      borderColor: active ? accentAlpha(0.55, isDark) : cardBorder,
                      backgroundColor: active
                        ? (accentAlpha(isDark ? 0.10 : 0.05, isDark))
                        : cardBg,
                      alignItems: 'center',
                    }}
                  >
                    <Image
                      source={pet.image}
                      style={{ width: 52, height: 52 }}
                      contentFit="contain"
                      accessibilityLabel={`${pet.name} the ${pet.species}`}
                    />
                    <Text
                      size="xs"
                      weight={active ? 'bold' : 'medium'}
                      style={{ marginTop: 8, color: active ? c.accent : undefined }}
                    >
                      {pet.name}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>

          <Text size="xs" color="secondary" style={{ marginTop: 24, textAlign: 'center' }}>
            Tap a companion to see it at work.
          </Text>
        </View>

        {/* ================================================================ */}
        {/* RECIPE & WORKOUT CARDS */}
        {/* ================================================================ */}
        <View
          style={[
            sectionStyle,
            {
              paddingVertical: isXS ? 60 : 100,
              backgroundColor: isDark ? 'rgba(255,255,255,0.01)' : 'rgba(0,0,0,0.01)',
            },
          ]}
        >
          <Text size="s" weight="medium" style={eyebrowStyle}>
            Rich Cards
          </Text>
          <Text
            tag="h2"
            weight="bold"
            style={{ fontSize: isXS ? 28 : 36, textAlign: 'center', marginBottom: 16, letterSpacing: -0.5 }}
          >
            Answers You Can Cook and Train From
          </Text>
          <Text
            size="m"
            color="secondary"
            style={{ textAlign: 'center', marginBottom: isXS ? 32 : 48, maxWidth: 640, lineHeight: 24 }}
          >
            Ask for dinner or a workout in Auto mode and the answer arrives as a beautiful,
            structured card — ingredients and steps, sets and reps — ready to save, share,
            or follow along.
          </Text>

          <View
            style={{
              flexDirection: isXS ? 'column' : 'row',
              flexWrap: 'wrap',
              gap: 20,
              justifyContent: 'center',
              alignItems: isXS ? 'stretch' : 'flex-start',
              maxWidth: 980,
              width: '100%',
            }}
          >
            <MockRecipeCard isDark={isDark} compact={isXS} />
            <MockWorkoutCard isDark={isDark} compact={isXS} />
          </View>
        </View>

        {/* ================================================================ */}
        {/* EDITABLE ARTIFACTS */}
        {/* ================================================================ */}
        <View style={[sectionStyle, { paddingVertical: isXS ? 60 : 100 }]}>
          <Text size="s" weight="medium" style={eyebrowStyle}>
            Editable Artifacts
          </Text>
          <Text
            tag="h2"
            weight="bold"
            style={{ fontSize: isXS ? 28 : 36, textAlign: 'center', marginBottom: 16, letterSpacing: -0.5 }}
          >
            Docs, Slides & Sheets You Can Edit
          </Text>
          <Text
            size="m"
            color="secondary"
            style={{ textAlign: 'center', marginBottom: isXS ? 32 : 48, maxWidth: 640, lineHeight: 24 }}
          >
            Ask for a brief, a deck, or a budget and Gab AI opens it as a living
            artifact — edit the document, rearrange the slides, or tweak the cells
            without starting over.
          </Text>

          <View
            style={{
              flexDirection: isXS ? 'column' : 'row',
              flexWrap: 'wrap',
              gap: 18,
              justifyContent: 'center',
              alignItems: isXS ? 'stretch' : 'stretch',
              maxWidth: 1140,
              width: '100%',
            }}
          >
            <MockDocumentArtifact isDark={isDark} compact={isXS} />
            <MockPresentationArtifact isDark={isDark} compact={isXS} />
            <MockSpreadsheetArtifact isDark={isDark} compact={isXS} />
          </View>
        </View>

        {/* ================================================================ */}
        {/* MORE IMPROVEMENTS */}
        {/* ================================================================ */}
        <View
          style={[
            sectionStyle,
            {
              paddingVertical: isXS ? 60 : 100,
              backgroundColor: isDark ? 'rgba(255,255,255,0.01)' : 'rgba(0,0,0,0.01)',
            },
          ]}
        >
          <Text size="s" weight="medium" style={eyebrowStyle}>
            And That's Not All
          </Text>
          <Text
            tag="h2"
            weight="bold"
            style={{ fontSize: isXS ? 28 : 36, textAlign: 'center', marginBottom: 16, letterSpacing: -0.5 }}
          >
            Richer Answers, Everywhere
          </Text>
          <Text
            size="m"
            color="secondary"
            style={{ textAlign: 'center', marginBottom: isXS ? 32 : 48, maxWidth: 640, lineHeight: 24 }}
          >
            From Work and local Mac projects to browser tools, custom bots, and
            better files — the latest additions join ongoing improvements to
            chat, search, memory, and mobile.
          </Text>

          <View
            style={{
              flexDirection: isXS ? 'column' : 'row',
              flexWrap: 'wrap',
              gap: 20,
              justifyContent: 'center',
              maxWidth: 1140,
              width: '100%',
            }}
          >
            {IMPROVEMENT_CARDS.map((card) => (
              <View
                key={card.title}
                style={{
                  flex: 1,
                  minWidth: isXS ? '100%' : 300,
                  maxWidth: isXS ? '100%' : 360,
                  padding: isXS ? 24 : 28,
                  borderRadius: 24,
                  backgroundColor: cardBg,
                  borderWidth: 1,
                  borderColor: cardBorder,
                }}
              >
                <View
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 14,
                    backgroundColor: accentAlpha(isDark ? 0.10 : 0.07, isDark),
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 18,
                  }}
                >
                  <Icon name={card.icon} size={22} color={c.accent} solid={card.icon === 'mobile'} />
                </View>
                <Text tag="h3" size="l" weight="medium" style={{ marginBottom: 8, letterSpacing: -0.3 }}>
                  {card.title}
                </Text>
                <Text size="s" color="secondary" style={{ lineHeight: 21 }}>
                  {card.body}
                </Text>
              </View>
            ))}
          </View>
        </View>

        {/* ================================================================ */}
        {/* NEW MODELS */}
        {/* ================================================================ */}
        <View
          style={[
            sectionStyle,
            {
              paddingVertical: isXS ? 60 : 100,
              backgroundColor: isDark ? 'rgba(255,255,255,0.01)' : 'rgba(0,0,0,0.01)',
            },
          ]}
        >
          <Text size="s" weight="medium" style={eyebrowStyle}>
            New Models
          </Text>
          <Text
            tag="h2"
            weight="bold"
            style={{ fontSize: isXS ? 28 : 36, textAlign: 'center', marginBottom: 16, letterSpacing: -0.5 }}
          >
            A Bigger Brain Trust
          </Text>
          <Text
            size="m"
            color="secondary"
            style={{ textAlign: 'center', marginBottom: isXS ? 32 : 48, maxWidth: 640, lineHeight: 24 }}
          >
            New chat, image, video, voice, and music models keep joining the picker — all
            available in the same workspace, on the same balance.
          </Text>

          <View style={{ maxWidth: 860, width: '100%', gap: 20 }}>
            {MODEL_GROUPS.map((group) => (
              <View
                key={group.label}
                style={{
                  padding: isXS ? 20 : 24,
                  borderRadius: 20,
                  backgroundColor: cardBg,
                  borderWidth: 1,
                  borderColor: cardBorder,
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <Icon name={group.icon} size={15} color={c.accent} />
                  <Text size="s" weight="bold" style={{ letterSpacing: 0.5, textTransform: 'uppercase', color: c.accent }}>
                    {group.label}
                  </Text>
                </View>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                  {group.models.map((model) => (
                    <View
                      key={model}
                      style={{
                        paddingVertical: 7,
                        paddingHorizontal: 14,
                        borderRadius: 999,
                        backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                        borderWidth: 1,
                        borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                      }}
                    >
                      <Text size="s" weight="medium">{model}</Text>
                    </View>
                  ))}
                </View>
              </View>
            ))}
          </View>
        </View>

        {/* ================================================================ */}
        {/* FINAL CTA */}
        {/* ================================================================ */}
        <View
          style={[
            sectionStyle,
            { paddingVertical: isXS ? 60 : 100 },
          ]}
        >
          <View
            style={{
              maxWidth: 700,
              width: '100%',
              padding: isXS ? 32 : 48,
              borderRadius: 28,
              backgroundColor: accentAlpha(isDark ? 0.07 : 0.04, isDark),
              borderWidth: 1,
              borderColor: accentAlpha(0.3, isDark),
              alignItems: 'center',
            }}
          >
            <Text
              tag="h2"
              weight="bold"
              style={{ fontSize: isXS ? 24 : 32, textAlign: 'center', marginBottom: 16, letterSpacing: -0.5 }}
            >
              Put the Latest Updates to Work
            </Text>
            <Text
              size="m"
              color="secondary"
              style={{ textAlign: 'center', marginBottom: 32, maxWidth: 480, lineHeight: 24 }}
            >
              Explore the new models, build your own bot, or start a project with
              Work. Chat is free to start with no credit card required; Work and
              live Voice Mode require a plan with access.
            </Text>
            <Button
              text="Start Exploring"
              variant="purple"
              size="large"
              isRound
              onPress={() => router.push('/signup')}
              style={{ minWidth: isXS ? '100%' : 260 }}
            />
          </View>
        </View>

        <PublicFooter />
      </ScrollView>
    </View>
  );
}
